import React, { createContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AOCScoringCategoryModel, AreaOfCompetitionTypeBaseModel, AwardTypeBaseModel, BasicContestantInfoDTO, ContestantAttributesModel, ContestantComprehensiveTabulationResultNew, EventDivision, EventDivisionBaseModel, EventJudgeModel, EventJudgeSummaryDTO, EventPersonnelModel, JudgeScore, PageantAgeGroupBaseModel, PageantAvailableTitleModel, PageantModel, ReceivedScoreBaseModel, RegisteredContestantAwardRelationshipBaseModel, RegisteredContestantSelectedTitleModel, RegistrationInvoice, ScoringCategoryBreakdown, SelectedAOC, StatusDetails } from 'API/Generated/data-contracts';
import { GetPageantStore } from 'SessionState/Pageant/PageantStore';
import { GetPageantAwardStore } from 'SessionState/Pageant/PageantAwardStore';
import { GetPageantAOCStore } from 'SessionState/Pageant/PageantAOCStore';
import { GetPageantRegistrationStore } from 'SessionState/Pageant/PageantRegistrationStore';
import { getAgeGroupName } from 'helpers/PageantHelper';
import { calcAgeAsOfYearsMonthsNew } from 'helpers/DateHelper';
import { GetEventStore } from 'SessionState/Event/EventStore';
import { GetUserStore } from 'SessionState/User/UserStore';
import { toast } from 'react-toastify';
import { GetTabulationStore } from 'SessionState/Tabulation/TabulationStore';
import { Modal, Spinner } from 'react-bootstrap';
import { Pageant } from 'API/Generated/Pageant';
import { BooleanLiteral } from 'typescript';
import { NumberListInstance } from 'twilio/lib/rest/pricing/v2/number';
import { computeTabulationStatus, TabulationStatus } from 'views/events/tabulation/components/computeTabulationStatus';
import { GetEventJudgeStore } from 'SessionState/Judge/EventJudgeStore';
import { useLiveScores } from 'hooks/useLiveScores';


//#region==========================HELPERS START=====================================================
export const formatScoreSimple = (score: number | undefined): string => {
  // Check if score is a number, if not return an empty string
  if (typeof score === 'number') {
    return score.toFixed(3); // Format the number to 3 decimal places
  }
  return ''; // Return an empty string if score is undefined
};

export const formatScore = (score: number | null | undefined): string => {
  if (score == null || Number.isNaN(score)) return '';

  // Round to 3 decimals (handles floating imprecision)
  const rounded = Math.round((score + Number.EPSILON) * 1000) / 1000;

  // Avoid "-0"
  const normalized = Object.is(rounded, -0) ? 0 : rounded;

  // Format with 3 decimals, then strip trailing zeros and optional dot
  return normalized.toFixed(3).replace(/\.?0+$/, '');
};


interface LoadingOverlayProps {
  show: boolean;
  text?: string; // Optional text to display in the loader
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ show, text }) => {
  return (
    <Modal show={show} centered backdrop="static" keyboard={false}>
      <Modal.Body className="text-center">
        <Spinner animation="border" variant="primary" />
        <div>{text || 'Loading tab provider...'}</div> {/* Display custom text or default text */}
      </Modal.Body>
    </Modal>
  );
};


type ScoresByContestant = Record<number, ReceivedScoreBaseModel[]>;

const getScoreContestantId = (score: any): number => {
  return Number(
    score?.registeredPageantContestantId ??
      score?.registeredContestantId ??
      score?.contestantId ??
      0
  );
};

const normalizeScoresByContestant = (
  input: ReceivedScoreBaseModel[] | ScoresByContestant | null | undefined
): ScoresByContestant => {
  if (!input) return {};

  if (Array.isArray(input)) {
    return input.reduce<ScoresByContestant>((acc, score) => {
      const contestantId = getScoreContestantId(score);

      if (!contestantId) return acc;

      if (!acc[contestantId]) {
        acc[contestantId] = [];
      }

      acc[contestantId].push(score);

      return acc;
    }, {});
  }

  return Object.entries(input).reduce<ScoresByContestant>((acc, [key, value]) => {
    const contestantId = Number(key);

    if (!contestantId) return acc;

    acc[contestantId] = Array.isArray(value)
      ? value
      : value
        ? [value as any]
        : [];

    return acc;
  }, {});
};

//#endregion==========================HELPERS END=====================================================



//#region============================OPTIONS START========================================
export const scoringCategoryOptions = [
  { value: 1, label: 'Facial Beauty' },
  { value: 2, label: 'Personality' },
  { value: 3, label: 'Poise' },
  { value: 4, label: 'Overall Impression' },
  { value: 5, label: 'Stage Presence' },
  { value: 6, label: 'Entertainment and Energy' },
];

export const buildPageantOptionLists = (pageant?: any) => {
  const ageGroups = pageant?.pageantAgeGroups ?? [];
  const levels = pageant?.pageantLevelGroups ?? [];

  const ageOptions: Opt[] = ageGroups
    .filter((g: any) => !g?.isSupreme)
    .map((g: any) => ({ value: Number(g.id), label: getAgeGroupName(g) }));

  const supremeOptions: Opt[] = ageGroups
    .filter((g: any) => !!g?.isSupreme)
    .map((g: any) => ({ value: Number(g.id), label: getAgeGroupName(g) }));

  const levelOptions: Opt[] = levels
    .map((l: any) => ({ value: Number(l.id), label: String(l.name ?? `Level ${l.id}`) }));

  return { ageOptions, supremeOptions, levelOptions };
};

type Opt = { value: number; label: string };

export const optionsCompeteStyles: Opt[] = [
  { value: 1, label: 'Glitz' },
  { value: 2, label: 'Natural' },
  { value: 3, label: 'N/A' },
];

export const genderOptions: Opt[] = [
  { value: 1, label: 'Female' },
  { value: 2, label: 'Male' },
];


//#endregion============================OPTIONS END========================================




//#region======= New Registered Contestant Objects - ===============
export interface RegisteredContestant {
  id: number;
  contestantNumber: string;
  firstName: string;
  lastName: string;
  fullName: string;
  headshot?: string;

  selectedPackageIds: number[];
  selectedTitleIds: number[];
  selectedEventIds: number[];
  mandatoryPackageIds: number[];

  contestantAreasOfCompetitionDetails: ContestantAOCSDetails[];
  contestantAwardDetails: ContestantAwardDetails[];

  attributes: ContestantAttributes;
  allScores: ReceivedScoreBaseModel[];
  grandTotalScore: number;
  supremeScore: ContestantSupremeTotalScoreNew;

  winnings: ContestantWonAward[],

  competitionStatus: ContestantCompetitionStatus,

  registrationInvoice?: RegistrationInvoice[];

  // plusMarksIndex?: Record<number, PlusMarksByAOC>;
}

export interface AwardTypeCalculationStatus {
  awardTypeId: number;
  awardTypeName: string;
  awardCount: number;
  enteredCount: number;
  scoredCount: number;
  enteredScoredCount?: number;
  isEnteredAwardsCalculated?: boolean;
  isAllAwardsCalculated?: boolean;
}

export interface ContestantCompetitionStatus {
  enteredAOCsTotal?: number;
  enteredAOCsCompleted?: number;
  hasIncompleteEnteredAOCs?: boolean;
  isReadyToScore?: boolean;

  // 🆕 Backend-provided status summary
  competitionStatus?: string;
  competitionStatusDetails?: string;

  calculationStatus?: string;
  calculationStatusDetails?: string;

  // renamed & expanded:
  awardsCalculationStatus: AwardTypeCalculationStatus[];
  totals: { awardCount: number; enteredCount: number; scoredCount: number };
}


export interface ContestantAttributes {
  id: number;
  ageGroupId: number;
  ageGroup: string;
  supremeGroups: string;
  supremeGroupIds: number[];
  level?: string;
  levelId?: number;
  gender: string;
  genderId: number;
  competeStyleId: number;
  competeStyle: string;
  eventDivisionId: number;
  eventDivision: string;

  // NEW (optional) fields coming from backend:
  contestantAgeDayOfPageant?: number | null;
  contestantAgeDayOfFallback?: number | null;
  startAgeOfContestantAgeGroup?: number | null;
  contestantTookFallback?: boolean | null;
  contestantMovedUpAgeGroup?: boolean | null;
  contestantAgeUsedForSupremeGroup?: number | null;
}

export interface ContestantAOCSDetails {
  aocId: number;
  aocTypeId: number;
  aocTypeName?: string;
  aocName: string;
  competeOrder?: number;
  isEntered: boolean;
  entrySource?: string;
  aocTotalScore?: number;
  aocMaxPossiblePoints?: number;
  aocTotalScoreWithDrops?: number;
  aocMaxPossiblePointsWithDrops?: number;
  maxPointsPossible?: number;
  aocComplete?: boolean;
  isUsedTowardSupremeScore?: boolean;
  auditNote?: string;
  status?: string;
  statusDetails?: string;
  judgeScores?: JudgeScore[];

  // plusMarks?: number;
  // plusMarksByCategory?: Record<number, number>;

  statusInfo?: StatusDetails[];
}

// export interface PlusMarksByAOC {
//   aocId: number;
//   total: number;                                 // sum of non-dropped judges across all cats
//   byCategory: Record<number, number>;            // key: aocScoreCategoryId -> plus marks
//   byJudge?: Record<number, {                     // key: judgeId
//     total: number;
//     byCategory: Record<number, number>;          // key: aocScoreCategoryId
//   }>;
// }






export interface ContestantAwardDetails {
  awardId: number;
  awardName: string;
  awardTypeId: number;
  awardTypeName: string;
  pullOrder?: number;
  awardScoringCriteriaSettings?: ScoringCriteria;
  awardScoringCriteria?: ScoringCriteria;
  isEntered: boolean;
  entrySource?: string;
  aocCalculationComplete?: boolean;
  awardTotalCalculatedScore?: number;
  maxPointsPossible?: number;
  auditNote?: string;
  numberAvailable?: number;
  doubleCrown?: boolean | null;              // NEW
  useSupremeScoringCriteria?: boolean | null;// NEW
  selectedAOC: SelectedAOC[];
  individualAvailableAwardsDetails: AwardIndexDetails[];
  totalPlusMarkCountInAwardScore?: number | undefined;
  statusInfo?: StatusDetails[];
}

export interface IndividualAwardWinners {
  awardIndex: number;
  criteriaUIString?: string;
  winningRegisteredContestantId: number;
  awardScore: number;
  isEntered: boolean;
  meetsCriteria: boolean;
}

export interface AwardIndexDetails { // details for each unique available award and its unique contestant criteria
  awardIndex: number;
  contestantCriteria: string;
  criteriaUIString?: string;
  isSavedAsEntered?: boolean;
  meetsContestantCriteria: boolean;
  isFullyEligible: boolean;
  statusDetails?: string;
  awardTotalCalculatedScore?: number;
  isWon?: boolean;
  auditNote?: string;
}

export interface ContestantWonAward {
  awardId: number;
  awardName: string;
  awardTypeId: number;
  awardTypeName: string;
  winningScore: number;
  auditNote?: string;

}

export interface ContestantSupremeTotalScoreNew {
  contestantId: number;
  calculatedScore: number;
  highlightedAocCount: number;
  highlightedAocs: HighlightedAOCDetailNew[];
  isTie?: boolean;
}

export interface HighlightedAOCDetailNew {
  aocId: number;
  aocName: string;
  finalScore: number;
}



export interface ContestantEnteredAwards {
  awardId: number;
  awardTypeId: number;
  awardName: string;
  entrySource?: string;
}







//#endregion======= New Registered Contestant Objects - testing===============


export interface ContestantSpecificAwardScoringDetails {
  contestantId: number;
  titleId: number;
  contestantName: string;
  awardScoringCriteriaSettings: {
    doubleCrown: boolean;
    dropLowestScore: boolean;
    dropHighestScore: boolean;
    titleId: number;
    customAwardRules: {
      topN: number;
      aocIds: number[];
      aocNames: string[];
      selectedAocType?: string;
      maxCountForSelectedAocType?: number;
      specificScoringCategoryIds?: number[];
    }[];
  };
  selectedAOCForTitleScore: {
    aocId: number;
    aocTypeId: number;
    aocName: string;
    aocScoreUsed: number;
    aocGrandTotal: number;
    aocTotalWithDropLowestScore: number;
    aocTotalWithDropHighestScore: number;
    aocTotalWithBothDrops: number;
    totalPossiblePoints: number;
    totalPossiblePointsWithDrop1Score: number;
    totalPossiblePointsWithDrop2Scores: number;
  }[];
  titleScore: number;
  titleScoreSummary: string;
}

export interface ContestantSupremeTotalScore {
  contestantId: number;
  calculatedScore: number;
  highlightedAocCount: number;
  highlightedAocs: HighlightedAOCDetail[];
  isTie?: boolean;
}

export interface HighlightedAOCDetail {
  aocId: number;
  aocName: string;
  finalScore: number;
  contestantScores: ReceivedScoreBaseModel[]; // Or whatever type you're using
}


//#region==============================PAGEANT AVAILABLE AWARDS OBJECT=================================
export interface PageantAvailableAward {
  id: number;
  name: string;
  pageantId: number;
  awardTypeId: number;
  awardType?: AwardTypeBaseModel; // Add this

  price?: number;
  cashPrize?: number;
  isSplitThePot?: boolean;
  numberAvailable: number;

  specificAwardFullCriteria?: SpecificAwardFullCriteria;
  awardScoringCriteria?: string;
  contestantEligibleWinnerCriteria?: string;

  pullOrder?: number;
  isReigningRoyalty?: boolean;
  note?: string;

  includedInPackages: IncludedInPackage[];
  enteredContestants: EnteredContestant[];

  matchingAwardRelationships?: MatchingAwardRelationships[];
}

export interface MatchingAwardRelationships {
  awardRelationship: RegisteredContestantAwardRelationshipBaseModel;
  criteriaUIString?: string;
}

export interface SpecificAwardFullCriteria {
  scoringCriteria: ScoringCriteria;
  additionalScoringDetails: AdditionalScoringDetails;
  contestantCriteria: ContestantCriteria[];
}

// export interface RegistrationInvoice {

// }

//#endregion==============================PAGEANT AVAILABLE AWARDS OBJECT=================================

//#region==============================PAGEANT AVAILABLE AREAS OF COMPETITION OBJECT=================================

export interface PageantAvailableAOC {
  id: number;
  name: string;
  pageantId: number;
  aocTypeId: number;
  aocType?: AreaOfCompetitionTypeBaseModel;
  competeOrder?: number;
  price: number;
  isOptional: boolean;
  aocScoreCategories: AOCScoringCategory[];
  note?: string;
  onAocsChange?: (newAocs: PageantAvailableAOC[]) => void;
  includedInPackages: IncludedInPackage[];
  availableToCompeteContestants: EnteredContestant[];
}


export interface AOCScoringCategory {
  id: number;
  scoringCategoryId: number;
  scoringCategoryName: string;
  minScore: number;
  maxScore: number;
}

//#endregion==============================PAGEANT AVAILABLE AREAS OF COMPETITION OBJECT=================================

export interface IncludedInPackage {
  pageantAvailablePackageId: number;
  packageName: string;
  note?: string;
}

export interface EnteredContestant {
  registeredPageantContestantId: number;
  contestantName: string;
  entrySource: string; // e.g., "Free award", "Selected directly", etc.
  status?: string;
}

export interface MeetsCriteriaContestant {
  registeredPageantContestantId: number;
  contestantName: string;
  meetsCriteriaSource: string; // e.g., "Meets age group", etc.
}

export interface FullyEligibleContestant {
  registeredPageantContestantId: number;
  contestantName: string;
  entrySource: string; // e.g., "Free award", "Selected directly", etc.
  meetsCriteriaSource: string; // e.g., "Meets age group", etc.
}

//=======FULL award criteria - new testing=========== eventually we will add the contestant criteria here as well and make it a FULL detailed version!


export interface AdditionalScoringDetails {
  isDoubleCrown?: boolean;
  calculationNeededToFindWinner?: boolean;
  isUseSupremeScoreSettings?: boolean;
}


// ====== Universal Scoring Criteria for Pageant - new testing ======
export interface CustomCriteriaRule {
  topN: number;
  aocIds: number[];
  aocNames: string[];
  selectedAocType?: string;
  selectedAocTypeId?: number;
  maxCountForSelectedAocType?: number | null;
  scoringCategoryIds?: number[];
  scoringCategoryNames?: string[];
}

export interface ScoringCriteria {
  customRules: CustomCriteriaRule[];
  dropLowestScore: boolean;
  dropHighestScore: boolean;
  maxPossiblePoints?: number;
}

// ====== Universal Contestant Criteria for Pageant - new testing ======


export interface SpecificContestantCriteria {
  ageGroupIds?: number[];
  supremeGroupIds?: number[];
  levelIds?: number[];
  genderIds?: number[];
  styleIds?: number[];
  divisionIds?: number[];

}

export interface ContestantCriteria {
  awardIndex: number;            // 1-based index per your example
  specificCriteria: SpecificContestantCriteria;
  criteriaUIString: string;
  meetsCriteriaContestants?: MeetsCriteriaContestant[];
  fullyEligibleContestants?: FullyEligibleContestant[];
}



//=================================================================================================================

export const generateDynamicScoringCriteriaString = (
  scoreSettings: ScoringCriteria,
  aocs: PageantAvailableAOC[],
  aocTypes: AreaOfCompetitionTypeBaseModel[],
  useDropLogic: boolean = false,
  includeAocCount: boolean = false,
  specialFormat: boolean = false
): string | React.ReactNode[] => {
  const rules: CustomCriteriaRule[] = scoreSettings.customRules;
  const dropLowest = scoreSettings.dropLowestScore;
  const dropHighest = scoreSettings.dropHighestScore;

  if (!rules.length) return specialFormat ? [''] : '';

  const totalAocsUsed = includeAocCount
    ? rules.reduce((sum, rule) => sum + (rule.topN ?? 0), 0)
    : 0;

  if (!specialFormat) {
    const plainDescriptions = rules.map(rule => {
      const aocNames = rule.aocIds.map(id => aocs.find(aoc => aoc.id === id)?.name ?? 'Unknown AOC').join(', ');
      let desc = rule.topN === 1 && rule.aocIds.length === 1
        ? aocNames
        : `Top ${rule.topN} score(s) from: ${aocNames}`;

      const categoryNames = (rule.scoringCategoryIds ?? [])
        .map(categoryId =>
          aocs
            .filter(aoc => rule.aocIds.includes(aoc.id))
            .flatMap(aoc => aoc.aocScoreCategories ?? [])
            .find(cat => cat.scoringCategoryId === categoryId)
        )
        .filter(Boolean)
        .map(cat => cat?.scoringCategoryName ?? (cat as any).scoringCategoryName ?? 'Unknown');

      if (categoryNames.length) {
        desc += ` (Using categories: ${categoryNames.join(', ')})`;
      }

      if (rule.selectedAocTypeId && rule.maxCountForSelectedAocType) {
        const aocTypeName = aocTypes.find(type => type.id === rule.selectedAocTypeId)?.name;
        desc += ` (Max ${rule.maxCountForSelectedAocType} of ${aocTypeName})`;
      }

      return desc;
    });

    let dropDesc = '';
    if (useDropLogic) {
      if (dropLowest && dropHighest) dropDesc = ' Dropping lowest and highest judge scores in each Event.';
      else if (dropLowest) dropDesc = ' Dropping lowest judge score in each Event.';
      else if (dropHighest) dropDesc = ' Dropping highest judge score in each Event.';
    }

    return (includeAocCount ? `(Made up of ${totalAocsUsed} Events.) ` : '') + plainDescriptions.join(' PLUS ') + dropDesc;
  }

  // ---- FORMATTED (specialFormat === true) ----
  const result: React.ReactNode[] = [];

  const metadataChunks: string[] = [];

  if (includeAocCount) {
    metadataChunks.push(`Made up of ${totalAocsUsed} Event${totalAocsUsed !== 1 ? 's' : ''}`);
  }

  if (useDropLogic) {
    if (dropLowest && dropHighest) {
      metadataChunks.push('Dropping lowest and highest judge scores in each Event');
    } else if (dropLowest) {
      metadataChunks.push('Dropping lowest judge score in each Event');
    } else if (dropHighest) {
      metadataChunks.push('Dropping highest judge score in each Event');
    }
  }

  if (metadataChunks.length) {
    result.push(
      <div key="metadata" className="text-small text-muted">
        {metadataChunks.join('   |   ')}
      </div>
    );
  }

  const ruleElements: React.ReactNode[] = [];

  rules.forEach((rule, index) => {
    const aocNames = rule.aocIds.map(id => aocs.find(a => a.id === id)?.name ?? 'Unknown Event');

    const categoryNames = (rule.scoringCategoryIds ?? [])
      .map(categoryId =>
        aocs
          .filter(aoc => rule.aocIds.includes(aoc.id))
          .flatMap(aoc => aoc.aocScoreCategories ?? [])
          .find(cat => cat.scoringCategoryId === categoryId)
      )
      .filter(Boolean)
      .map(cat => cat?.scoringCategoryName ?? (cat as any).scoringCategoryName ?? 'Unknown');

    const categoryDisplay = categoryNames.length > 0
      ? ` (Using categories: ${categoryNames.join(', ')})`
      : '';

    const maxTypeDisplay = rule.selectedAocTypeId && rule.maxCountForSelectedAocType
      ? ` (Max ${rule.maxCountForSelectedAocType} of ${aocTypes.find(type => type.id === rule.selectedAocTypeId)?.name
      })`
      : '';

    const ruleText = (
      <span className="text-small" key={`rule-${index}`}>
        <strong>
          {rule.topN === 1 && aocNames.length === 1
            ? aocNames[0]
            : `Top ${rule.topN} score(s) from: ${aocNames.join(', ')}`}
        </strong>
        {categoryDisplay}
        {maxTypeDisplay}
      </span>
    );

    ruleElements.push(ruleText);

    if (index < rules.length - 1) {
      ruleElements.push(
        <span key={`plus-${index}`} className="text-small mx-2 font-semibold text-danger">
          PLUS
        </span>
      );
    }
  });

  result.push(
    <div key="inlineRules" className="flex flex-wrap gap-x-2 gap-y-1 items-center">
      {ruleElements}
    </div>
  );

  return result;

};


//==========PARSING THE SCORING CRITERIA, CONTESTANT CRITERIA AND ADDITIONAL DETAIL BOOLS - testing
export const parseContestantCriteriaNew = (
  contestantCriteriaString: string | null | undefined
): ContestantCriteria[] => {
  if (!contestantCriteriaString || !contestantCriteriaString.trim()) return [];

  let arr: any;
  try {
    arr = JSON.parse(contestantCriteriaString);
  } catch {
    return [];
  }
  if (!Array.isArray(arr)) return [];

  const toNumArr = (v: any): number[] =>
    Array.isArray(v)
      ? Array.from(
        new Set(
          v
            .map((x) => Number(x))
            .filter((n) => Number.isFinite(n) && n !== 0) // drop 0 = “ALL”
        )
      )
      : [];

  return arr
    .map((item: any): ContestantCriteria | null => {
      const idx = Number(item?.awardIndex);
      if (!idx) return null;

      // Support BOTH legacy {criteria:{...}} and new {specificCriterias:[{...}]}
      const raw =
        item?.criteria ??
        (Array.isArray(item?.specificCriterias) && item.specificCriterias.length > 0
          ? item.specificCriterias[0]
          : {}) ??
        {};

      return {
        awardIndex: idx,
        specificCriteria: {
          ageGroupIds: toNumArr(raw.ageGroupIds),
          supremeGroupIds: toNumArr(raw.supremeGroupIds),
          levelIds: toNumArr(raw.levelIds),
          genderIds: toNumArr(raw.genderIds),
          // accept either competeStyleIds or styleIds
          styleIds: toNumArr(raw.competeStyleIds ?? raw.styleIds),
          divisionIds: toNumArr(raw.divisionIds),
        },
        criteriaUIString: formatContestantCriteria(raw)
      };
    })
    .filter((x): x is ContestantCriteria => !!x)
    .sort((a, b) => a.awardIndex - b.awardIndex);
};


export const parseScoringCriteriaNew = (
  scoringCriteriaString: string | null | undefined
): ScoringCriteria => {
  try {
    const raw = scoringCriteriaString ? JSON.parse(scoringCriteriaString) : null;

    if (!raw) {
      throw new Error("No criteria data");
    }

    return {
      dropLowestScore: raw.dropLowestScore ?? false,
      dropHighestScore: raw.dropHighestScore ?? false,
      customRules: Array.isArray(raw.rules)
        ? raw.rules.map((r: any) => r.criteria)
        : []
    };
  } catch (err) {
    console.warn("❌ Failed to parse scoring criteria:", err);
    return {
      dropLowestScore: false,
      dropHighestScore: false,
      customRules: [],
    };
  }
};

export const parseAdditionalScoringDetailsOrg = (
  award: any
): AdditionalScoringDetails => {
  try {
    return {
      isUseSupremeScoreSettings: !!award?.useSupremeScoringCriteria,
      calculationNeededToFindWinner: !!award?.calculationNeeded,
      isDoubleCrown: !!award?.doubleCrown,
    };
  } catch (err) {
    console.warn("❌ Failed to parse additional scoring details:", err);
    return {
      isUseSupremeScoreSettings: false,
      calculationNeededToFindWinner: false,
      isDoubleCrown: false,
    };
  }

};

export const parseAdditionalScoringDetails = (
    award: any
): AdditionalScoringDetails => {
    try {
        const firstRelationship =
            award?.matchingAwardRelationships?.[0]
                ?.awardRelationship ??
            award?.matchingAwardRelationships?.[0]
                ?.registeredContestantAwardRelationship ??
            null;

        const isUseSupremeScoreSettings: boolean =
            award?.useSupremeScoringCriteria ??
            firstRelationship?.useSupremeScoringCriteria ??
            false;

        const calculationNeededToFindWinner: boolean =
            award?.calculationNeeded ??
            firstRelationship?.calculationNeeded ??
            false;

        const isDoubleCrown: boolean =
            award?.doubleCrown ??
            firstRelationship?.doubleCrown ??
            false;

        return {
            isUseSupremeScoreSettings:
                !!isUseSupremeScoreSettings,

            calculationNeededToFindWinner:
                !!calculationNeededToFindWinner,

            isDoubleCrown:
                !!isDoubleCrown,
        };
    } catch (err) {
        console.warn(
            "❌ Failed to parse additional scoring details:",
            err
        );

        return {
            isUseSupremeScoreSettings: false,
            calculationNeededToFindWinner: false,
            isDoubleCrown: false,
        };
    }
};

export const getContestantCriteriaDescriptionsForAward = (
    award: PageantAvailableAward | null | undefined
): string[] => {
    if (!award) {
        return [];
    }

    const descriptions = new Map<string, string>();

    const addDescription = (
        value: string | null | undefined
    ): void => {
        const normalized = value
            ?.trim()
            .replace(/\s+/g, " ");

        if (!normalized) {
            return;
        }

        const key = normalized.toLowerCase();

        if (!descriptions.has(key)) {
            descriptions.set(key, normalized);
        }
    };

    // 1. PRIMARY SOURCE:
    // Award-level contestant criteria stored on PageantAvailableTitle.
    for (
        const criteria of
        award.specificAwardFullCriteria?.contestantCriteria ?? []
    ) {
        addDescription(
            criteria.criteriaUIString ||
            formatContestantCriteria(
                criteria.specificCriteria
            )
        );
    }

    // 2. FALLBACK / SUPPLEMENTAL SOURCE:
    // Existing AwardRelationships, when contestants exist.
    for (
        const relationship of
        award.matchingAwardRelationships ?? []
    ) {
        addDescription(
            relationship?.criteriaUIString
        );

        addDescription(
            relationship?.awardRelationship
                ?.awardIndexContestantCriteriaDescription
        );
    }

    return Array.from(descriptions.values());
};


//====Title and corresponding award relationships=======
export interface SpecificAward {
  id: number;
  name: string;
  awardScoringCriteria: string;
  contestantEligibleWinnerCriteria: string;
}

export interface ContestantAwardRelationship {
  id: number;
  pageantAvailableTitleId: number;
  registeredPageantContestantId: number;
  calculationNeeded?: boolean;
  doubleCrown?: boolean;
  useSupremeScoringCriteria?: boolean;
  awardScoringCriteriaDescription?: string;
  awardIndexContestantCriteriaDescription?: string;
  isEntered: boolean;
  meetsContestantCriteria: boolean;
  awardScoreBasedOnScoringCriteria?: number;
  awardIndex: number;
}

export interface AwardWithCorrespondingRelationships {
  award: SpecificAward;
  awardRelationships: ContestantAwardRelationship[];
}




export const getTotalAocScoreCount = (rules: CustomCriteriaRule[] | undefined | null): number => {
  if (!Array.isArray(rules)) return 0;

  return rules.reduce((total, rule) => {
    const topN = typeof rule.topN === 'number' && !isNaN(rule.topN) ? rule.topN : 0;
    return total + topN;
  }, 0);
};



export const generateScoringCriteriaStringNew2 = (
  customRules: CustomCriteriaRule[],
  aocs: PageantAvailableAOC[],
  aocTypes: AreaOfCompetitionTypeBaseModel[]
): string => {
  if (!customRules.length) return 'No scoring criteria available';

  return customRules.map(rule => {
    const aocNames = rule.aocIds
      .map(id => aocs.find(aoc => aoc.id === id)?.name || 'Unknown AOC')
      .join(', ');

    let description = `Top ${rule.topN} score(s) from: ${aocNames}`;

    // Category filter
    if (rule.scoringCategoryIds?.length) {
      const selectedCategories = rule.scoringCategoryIds
        .map(categoryId =>
          aocs
            .filter(aoc => rule.aocIds.includes(aoc.id))
            .flatMap(aoc => aoc.aocScoreCategories || [])
            .find(cat => cat.scoringCategoryId === categoryId)
        )
        .filter(Boolean);

      const categoryNames = selectedCategories
        .map(cat => cat?.scoringCategoryName ?? 'Unknown Category')
        .join(', ');

      if (categoryNames) {
        description += ` (Using categories: ${categoryNames})`;
      }
    }

    // Max of specific AOC type logic
    const hasValidAocType = rule.selectedAocTypeId;
    const hasValidMax = rule.maxCountForSelectedAocType != null && rule.maxCountForSelectedAocType > 0;

    if (hasValidAocType && hasValidMax) {
      const aocTypeName = aocTypes.find(type => type.id === rule.selectedAocTypeId)?.name;
      description += ` (Max ${rule.maxCountForSelectedAocType} of ${aocTypeName})`;
    }

    return description;
  }).join(' PLUS ');
};
export const generateBasicScoringCriteriaStringFromCriteriaSettings = (
  criteria: string | ScoringCriteria | null,
  aocs: PageantAvailableAOC[],
  aocTypes: AreaOfCompetitionTypeBaseModel[]
): React.ReactNode[] => {
  if (!criteria) return ['No scoring criteria available.'];

  let parsedCriteria: ScoringCriteria;

  try {
    parsedCriteria = typeof criteria === 'string' ? JSON.parse(criteria) : criteria;
  } catch (err) {
    console.error('❌ Error parsing scoring criteria JSON:', err);
    return ['Invalid scoring criteria'];
  }

  if (!parsedCriteria?.customRules || parsedCriteria.customRules.length === 0) {
    return ['No scoring criteria available.'];
  }

  const { customRules, dropLowestScore, dropHighestScore } = parsedCriteria;

  const results: React.ReactNode[] = customRules.map((rule, index) => {
    const {
      topN,
      aocIds = [],
      aocNames = [],
      scoringCategoryIds = [],
      maxCountForSelectedAocType,
      selectedAocType,
    } = rule;

    const resolvedAocNames = aocNames.length > 0
      ? aocNames
      : aocIds.map(id => aocs.find(a => a.id === id)?.name ?? 'Unknown');

    const lowerAocNames = resolvedAocNames.map(name => name.toLowerCase());
    const allAreBeauty = lowerAocNames.every(name => name.includes('beauty'));
    const singleBeauty = resolvedAocNames.length === 1 && lowerAocNames[0].includes('beauty');

    const categoryNames = scoringCategoryIds
      .map(id => scoringCategoryOptions.find(cat => cat.value === id)?.label || `Category ${id}`)
      .join(', ');

    const categorySpan = scoringCategoryIds.length > 0 && (
      <span className="text-small"> using only <strong>{categoryNames}</strong></span>
    );

    const resolvedAocTypeName = aocTypes.find(
      type => type.name === selectedAocType
    )?.name ?? selectedAocType;

    const maxTypeSpan = selectedAocType && typeof maxCountForSelectedAocType === 'number' && (
      <span className="text-small"> (max {maxCountForSelectedAocType} {resolvedAocTypeName})</span>
    );

    const aocSpan = resolvedAocNames.length > 0 && (
      <span className="text-small">
        from <strong>{resolvedAocNames.join(', ')}</strong>
      </span>
    );

    const prefix = index === 0 ? '' : 'PLUS ';

    const baseText = singleBeauty
      ? <>Best {resolvedAocNames[0]}</>
      : allAreBeauty
        ? <>Best {topN === 1 ? 'Beauty' : `${topN} Beauty events`}</>
        : (topN === 1 && resolvedAocNames.length === 1)
          ? <>{resolvedAocNames[0]}</>
          : <>Best {topN} {aocSpan}</>;

    return (
      <div key={index}>
        {prefix}
        {baseText}
        {categorySpan}
        {maxTypeSpan}
      </div>
    );
  });

  if (dropLowestScore || dropHighestScore) {
    let dropText = 'Drop ';
    if (dropLowestScore && dropHighestScore) dropText += 'lowest and highest judge scores';
    else if (dropLowestScore) dropText += 'lowest judge score';
    else if (dropHighestScore) dropText += 'highest judge score';

    results.push(<div className="text-small text-muted" key="dropLogic">{dropText}</div>);
  }

  return results;
};




//========General Award SCORING Criteria START========

//========General Award SCORING Criteria END========

//========General Award CONTESTANT Criteria START========

export interface CriteriaLookups {
  // NEW: pass the full lists so we can use getAgeGroupNameById(...)
  ageGroups?: readonly PageantAgeGroupBaseModel[];
  supremeGroups?: readonly PageantAgeGroupBaseModel[];

  // Optional string maps (still supported)
  ageGroupById?: Record<number, string>;
  supremeGroupById?: Record<number, string>;
  levelById?: Record<number, string>;
  styleById?: Record<number, string>;
  genderById?: Record<number, string>;
  divisionById?: Record<number, string>;
}







type Maps = {
  groupNameById: Record<number, string>;  // age + supreme in one table
  levelById: Record<number, string>;
  divisionById: Record<number, string>;
};

let _maps: Maps = { groupNameById: {}, levelById: {}, divisionById: {} };

export const primeCriteriaRegistryFromPageant = (pageant?: any) => {
  const ageGroups = pageant?.pageantAgeGroups ?? [];
  const levels = pageant?.pageantLevelGroups ?? [];
   const divisions =
    pageant?.eventDivisions ??
    pageant?.eventDivisionIds ?? 
    pageant?.pageantDivisions ??
    [];

  _maps.groupNameById = Object.fromEntries(
    ageGroups
      .filter((g: any) => g?.id !== undefined)
      .map((g: any) => [Number(g.id), getAgeGroupName(g)])
  );

  _maps.levelById = Object.fromEntries(
    levels
      .filter((l: any) => l?.id !== undefined)
      .map((l: any) => [Number(l.id), String(l.name ?? `Level ${l.id}`)])
  );

  _maps.divisionById = Object.fromEntries(
    divisions
      .filter((d: any) => d?.id !== undefined)
      .map((d: any) => [Number(d.id), String(d.name ?? `Division ${d.id}`)])
  );
};

export const getCriteriaRegistry = (): Maps => _maps;



const uniq = <T,>(arr: T[]) => Array.from(new Set(arr));
const toNumArr = (v: any): number[] =>
  Array.isArray(v) ? uniq(v.map(Number).filter(n => Number.isFinite(n) && n !== 0)) : [];

const asSpecific = (raw: any) => ({
  ageGroupIds: toNumArr(raw?.ageGroupIds),
  supremeGroupIds: toNumArr(raw?.supremeGroupIds),
  levelIds: toNumArr(raw?.levelIds),
  genderIds: toNumArr(raw?.genderIds),
  styleIds: toNumArr(raw?.styleIds ?? raw?.competeStyleIds),
  divisionIds: toNumArr(raw?.divisionIds ?? raw?.eventDivisionIds),
});

const allEmpty = (c: any) =>
  [c.ageGroupIds, c.supremeGroupIds, c.levelIds, c.genderIds, c.styleIds, c.divisionIds]
    .every(xs => !xs || xs.length === 0);

export const formatContestantCriteria = (
  input: string | any | undefined,
  opts?: { debug?: boolean }
): string => {
  if (!input) return "Available to All.";

  // If caller passed an object with backend text, trust it.
if (typeof input === "object" && input && typeof input.criteriaUIString === "string") {
  const hasSpecificCriteria =
    input.specificCriteria ||
    input.specificCriterias ||
    input.criteria ||
    input.divisionIds ||
    input.ageGroupIds ||
    input.supremeGroupIds ||
    input.levelIds ||
    input.genderIds ||
    input.styleIds;

  const s = input.criteriaUIString.trim();

  if (s && !hasSpecificCriteria) return s;
}

  // If it's a plain string, try to parse; else return it as-is
  if (typeof input === "string") {
    try {
      const parsed = JSON.parse(input);
      input = parsed;
    } catch {
      return input.trim() ? input : "Available to All.";
    }
  }

  // If it’s a ContestantCriteria, drill into .specificCriteria
let raw: any = input;

if (typeof raw?.awardIndex === "number" && raw?.specificCriteria) {
  raw = raw.specificCriteria;
}

if (raw?.criteria) raw = raw.criteria;

if (Array.isArray(raw?.specificCriterias) && raw.specificCriterias.length) {
  raw = raw.specificCriterias[0];
}

if (Array.isArray(raw) && raw.length) {
  const first = raw[0];
  raw = first?.specificCriterias?.[0] ?? first?.specificCriteria ?? first?.criteria ?? first;
}

  // ... your existing mapping logic remains unchanged below ...
  const c = asSpecific(raw);
  if (allEmpty(c)) return "Available to All.";

  const maps = getCriteriaRegistry();
  const genderRec: Record<number | string, string> = { 1: "Female", 2: "Male" };
  const styleRec: Record<number | string, string> = { 1: "Glitz", 2: "Natural" };

  const mapBy = (ids: number[] | undefined, rec?: Record<number | string, string>, fallback?: string) =>
    (ids ?? []).map((id) => rec?.[id] ?? rec?.[String(id)] ?? (fallback ? `${fallback} ${id}` : String(id)));

  const ages = mapBy(c.ageGroupIds, maps.groupNameById, "Age Group");
  const supremes = mapBy(c.supremeGroupIds, maps.groupNameById, "Supreme Group");
  const levels = mapBy(c.levelIds, maps.levelById, "Level");
  const styles = mapBy(c.styleIds, styleRec, "Style");
  const genders = mapBy(c.genderIds, genderRec, "Gender");
  const divisions = mapBy(c.divisionIds, maps.divisionById, "Division");

  console.log("criteria registry maps", getCriteriaRegistry());
console.log("divisionIds", c.divisionIds);

  if (opts?.debug) {
    console.debug("[formatContestantCriteria] mapped", { c, ages, supremes, levels, styles, genders, divisions });
  }

  const groups = [styles, genders, ages, supremes, levels, divisions].map((g) => g.filter(Boolean)).filter((g) => g.length);
  return groups.length ? groups.map((g) => g.join(", ")).join(" & ") : "Available to All.";
};


//========General Award CONTESTANT Criteria END========


//======================AWARD SCORING PROCESS Start==================

type AwardCalcProgress = {
  running: boolean;
  phase: 'idle' | 'supreme' | 'awards' | 'done' | 'error';
  currentAwardId?: number;
  currentAwardName?: string;
  currentIndex: number;
  totalAwards: number;
  completed: number;
  resultsByAwardId: Record<number, any>;
  supremeAwardIds?: number[]; // NEW: columns to glow during "supreme" phase
};





//======================AWARD SCORING PROCESS End==================

// Define the type for context data
interface TabulationDataContextType {
  //ALl Personnel -  emcee, etc
  personnel: EventPersonnelModel[];
  setPersonnel: React.Dispatch<React.SetStateAction<EventPersonnelModel[]>>;

  //ALl Jugdges
  judges: EventJudgeModel[];
  setJudges: React.Dispatch<React.SetStateAction<EventJudgeModel[]>>;
  getJudgeCountForAoc: (aocId: number) => number;
  getAocMaxPoints: (
    aocId: number,
    settings?: ScoringCriteria,
    withDrops?: boolean
  ) => number;
  getSupremeMaxPointsForContestant: (contestantId: number) => number;

  //All age Groups in a pageant
  ageGroupsOrdered: PageantAgeGroupBaseModel[];
  setAgeGroupsOrdered: React.Dispatch<React.SetStateAction<PageantAgeGroupBaseModel[]>>;

  //All divisions in a pageant
  eventDivisions: EventDivisionBaseModel[];
  setEventDivisions: React.Dispatch<React.SetStateAction<EventDivisionBaseModel[]>>;

  //All supreme Groups in a pageant
  supremeGroupsOrdered: PageantAgeGroupBaseModel[];
  setSupremeGroupsOrdered: React.Dispatch<React.SetStateAction<PageantAgeGroupBaseModel[]>>;

  scoresForAOCCompare: { [contestantId: number]: { [aocId: number]: { [categoryName: string]: number[] } } };
  setScoresForAOCCompare: React.Dispatch<React.SetStateAction<{ [contestantId: number]: { [aocId: number]: { [categoryName: string]: number[] } } }>>;

  //All individual scores for all categories in all aocs from all judges for all contestants in a pageant
  allFetchedScores: { [contestantId: number]: ReceivedScoreBaseModel[] };
  setAllFetchedScores: React.Dispatch<React.SetStateAction<{ [contestantId: number]: ReceivedScoreBaseModel[] }>>;

  // All Supreme Scores per contestant
  allSupremeScores: { [contestantId: number]: ContestantSupremeTotalScore };
  setAllSupremeScores: React.Dispatch<React.SetStateAction<{ [contestantId: number]: ContestantSupremeTotalScore }>>;

  liveScoreNotifications: any[];
setLiveScoreNotifications: React.Dispatch<React.SetStateAction<any[]>>;
clearLiveScoreNotifications: () => void;


  //All Awards available in a pageant
  pageantAvailableAwards: PageantAvailableAward[];
  setPageantAvailableAwards: React.Dispatch<React.SetStateAction<PageantAvailableAward[]>>;

  //All AOCs available in a pageant
  pageantAvailableAOC: PageantAvailableAOC[];
  setPageantAvailableAOC: React.Dispatch<React.SetStateAction<PageantAvailableAOC[]>>;

  fetchContestantCriteriaAndScoringCriteria: (titleId: number) => Promise<PageantAvailableTitleModel | null>;
  //All Award Relationships - these are the relationship between an award(pageantavailabletitle) and contestant: entered? eligible? score? etc
  awardRelationships: RegisteredContestantAwardRelationshipBaseModel[];
  setAwardRelationships: React.Dispatch<React.SetStateAction<RegisteredContestantAwardRelationshipBaseModel[]>>;
  //ALl Award Types - titles, side awards, jackpots, etc
  awardTypes: AwardTypeBaseModel[];
  setAwardTypes: React.Dispatch<React.SetStateAction<AwardTypeBaseModel[]>>;
  //Award Calculations
  // Orchestrated recalc (Supreme first, then per-award)
  runAwardRecalcSequence: (
    filters: { pageantId?: number; pageantAvailableTitleId?: number; registeredContestantId?: number },
    opts?: { awardIdsOverride?: number[]; refreshEachStep?: boolean }
  ) => Promise<void>;
  cancelAwardRecalcSequence: () => void;

  // Live progress for UI
  awardCalcProgress: AwardCalcProgress;
  //All Areas of Competition
  // aocs: AOC[];
  // setAocs: React.Dispatch<React.SetStateAction<AOC[]>>;
  //All aoc types - stage event, photo event, etc
  aocTypes: AreaOfCompetitionTypeBaseModel[];
  setAocTypes: React.Dispatch<React.SetStateAction<AreaOfCompetitionTypeBaseModel[]>>;
  //All registered contestants
  // registeredContestants: Contestant[];

  registeredContestantsNew: RegisteredContestant[];
  setRegisteredContestantsNew: React.Dispatch<React.SetStateAction<RegisteredContestant[]>>;

  // fetchRegisteredContestantsDynamic: (
  //   params: { pageantId?: number; registeredContestantIds?: number[] },
  //   opts?: { replaceAll?: boolean }
  // ) => Promise<RegisteredContestant[]>;

fetchRegisteredContestantsDynamic: (
  params: { pageantId?: number; registeredContestantIds?: number[] },
  opts?: {
    replaceAll?: boolean;
    silent?: boolean;
   scoresByContestantOverride?: ScoresByContestant;
  }
) => Promise<RegisteredContestant[]>;

  updateEligibilityFlagsDynamic: (filters?: {
    registeredContestantId?: number;
    pageantAvailableTitleId?: number;
    pageantId?: number;
  }) => Promise<void>;

  currentPageant: Pageant[];
  setCurrentPageant: React.Dispatch<React.SetStateAction<Pageant[]>>;

  supremeScoringSettings: ScoringCriteria;
  setSupremeScoringSettings: React.Dispatch<React.SetStateAction<ScoringCriteria>>;
  handleSupremeScoreSetupNew: (supremeSettings: ScoringCriteria) => void; // Add this line
  saveSupremeScoreCriteriaToStore: (
    pageantId: number,
    criteria: ScoringCriteria,
    opts?: {
      updateAwardsUsingSupreme?: boolean;
      resetAwardsUsingSupreme?: boolean;
      calculateScores?: boolean;
    }
  ) => Promise<void>;


  //=============Resets Start=============

  resetAwardScoringCriteriaDynamic: (filters: {
    pageantAvailableTitleIds?: number[];
    pageantId?: number;
  }) => Promise<any[] | null>;

  resetAwardRelationshipScoresDynamic: (filters: {
    pageantAvailableTitleIds?: number[];
    registeredContestantIds?: number[];
    pageantId?: number;
  }) => Promise<any[] | null>;

  //=============Refetches Start=============


  refetchAwardRelationships: (filters?: {
    registeredContestantId?: number;
    pageantAvailableTitleId?: number;
    pageantId?: number;
  }) => Promise<void>;

  refetchPageantAvailableAwards: (filters?: {
    pageantId?: number;
    titleId?: number;
    awardTypeId?: number;
    registeredPageantContestantId?: number;
    pageantAvailablePackageId?: number;
  }) => Promise<void>;

  refetchPageantAvailableAOC: (filters?: {
    pageantId?: number;
    aocId?: number;
    aocTypeId?: number;
    registeredPageantContestantId?: number;
    pageantAvailablePackageId?: number;
  }) => Promise<void>;

  refetchReceivedScores: (
    filters?: {
      pageantId?: number;
      registeredPageantContestantId?: number;
      judgeId?: number;
      aocId?: number;
      aocScoringCategoryId?: number;
    },
    opts?: { skipPresenceCheck?: boolean }
  ) => Promise<ReceivedScoreBaseModel[]>;

  refetchSupremeCriteriaAndSupremeScores: () => Promise<void>;

  refetchRegisteredContestants: () => Promise<void>;

  //=============Refetches End=============

  totalPointsPossible: { [key: number]: number };
  setTotalPointsPossible: React.Dispatch<React.SetStateAction<{ [key: number]: number }>>;
  fetchAllStarted: boolean;
  setFetchAllStarted: React.Dispatch<React.SetStateAction<boolean>>;
  isDataLoaded: boolean;
  setIsDataLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadingScoring: boolean;
  loadingText: string | undefined;
  showLoader: (text: string) => void;
  hideLoader: () => void;
  existingAOCScoringCategories: { [key: number]: AOCScoringCategoryModel[] }; // Add new field here
  setExistingAOCScoringCategories: React.Dispatch<React.SetStateAction<{ [key: number]: AOCScoringCategoryModel[] }>>; // Add new setter here

  //==================AWARD CRITERIA============

  normalizeAwardCriteria: (criteria: any, pageant: any) => {
    ageGroups: string[];
    ageGroupIds: number[];
    supremeGroups: string[];
    supremeGroupIds: number[];
    divisionIds: number[];
    division: string[];
    levels: string[];
    levelIds: number[];
    competeStyles: string[];
    competeStyleIds: number[];
    genders: string[];
  };

  // customJudgeNames: { [aocId: number]: { [judgeIndex: string]: string } }; // Add this line
  // setCustomJudgeNames: React.Dispatch<
  //   React.SetStateAction<{ [aocId: number]: { [judgeIndex: string]: string } }>
  // >;
  //================SELECTED CONTESTANT==============
  selectedContestant: RegisteredContestant | null; // Add selected contestant state
  setSelectedContestant: React.Dispatch<React.SetStateAction<RegisteredContestant | null>>; // Setter for selected contestant

  fetchContestantAttributes: (pageantId: number) => void;


  //================Methods for calculations=================
  fetchTotalScoresWithDropsForContestantAsyncAll: (contestantId: number) => Promise<{ [key: string]: any }>;
  highlightAOCsUsedForMasterScoreNew: (contestantId: number) => number[];

  orderPageantAgeGroupsYoungestToOldest: (ageGroups: PageantAgeGroupBaseModel[]) => PageantAgeGroupBaseModel[];



  calculateFinalScoreWithDrops: (aocId: number, contestantId: number) => { finalScore: number; droppedScores: number[] };
  calculateAOCTotalScore: (aocId: number, judgeIndex: number, contestantId: number) => number;
  calculateAOCFinalScoreWithDrops: (aocId: number, contestantId: number) => { finalScore: number; droppedScores: number[] };

  //================STORES=================
  pageantStore: any;
  eventStore: any;
  userStore: any;
  judgeStore: any;
  pageantRegistrationStore: any;
  awardStore: any;
  tabulationStore: any;
  aocStore: any;

  pageant: any;

  isScoringAndAdmin: boolean | undefined;

  //==============--Fetch Winner Data============




  //==================FETCH ALL SCORING AND TALLYING DATA =================
  // fetchAllContestantScores: () => Promise<Record<number, ReceivedScoreBaseModel[]>>;


  //  fetchAllScoreAndTallyData: () => Promise<void>; // Adjust return type if needed
  fetchAllScoreAndTallyData: (pageantId: number) => Promise<void>;

  checkScoresUpToDate: () => void;

  //==================== HELPERS ====================
  contestantBasicInfoForPageant: { [contestantId: number]: string };
  setContestantBasicInfoForPageant: React.Dispatch<React.SetStateAction<{ [contestantId: number]: string }>>;
  fetchBasicContestantInfo: (contestantId: number | null) => Promise<{
    fullName: string;
    contestantNumber: string;
  }>;
  // fetchAllBasicContestantInfoForPageant: (pageantId: number) => Promise<void>;
  fetchTitleDetails: (titleId: number) => Promise<{ name: string; pullOrder: number }>;

  //Tabulation Status
  tabulationStatus: TabulationStatus | null;
  //refreshTabulationStatus: () => void;

}

// Provide a default value matching the type (e.g., empty arrays and empty objects)
const defaultTabulationDataContext: TabulationDataContextType = {
  personnel: [],
  setPersonnel: () => { },

  judges: [],
  setJudges: () => { },
  getJudgeCountForAoc: () => 0,

  getAocMaxPoints: () => 0,

  getSupremeMaxPointsForContestant: () => 0,

  ageGroupsOrdered: [],
  setAgeGroupsOrdered: () => { },

  eventDivisions: [],
  setEventDivisions: () => { },

  supremeGroupsOrdered: [],
  setSupremeGroupsOrdered: () => { },

  // aocs: [],
  // setAocs: () => { },

  liveScoreNotifications: [],
setLiveScoreNotifications: () => {},
clearLiveScoreNotifications: () => {},

  scoresForAOCCompare: {},
  setScoresForAOCCompare: () => { },
  allFetchedScores: {},
  setAllFetchedScores: () => { },
  refetchReceivedScores: async (
    _filters?: {
      pageantId?: number;
      registeredPageantContestantId?: number;
      judgeId?: number;
      aocId?: number;
      aocScoringCategoryId?: number;
    },
    _opts?: { skipPresenceCheck?: boolean }
  ): Promise<ReceivedScoreBaseModel[]> => {
    return [];
  },

  allSupremeScores: {},
  setAllSupremeScores: () => { },
  refetchSupremeCriteriaAndSupremeScores: async () => { },

  checkScoresUpToDate: () => { },
  // registeredContestants: [],

  registeredContestantsNew: [],
  setRegisteredContestantsNew: () => { },

  fetchRegisteredContestantsDynamic: async () => [],

  refetchRegisteredContestants: async () => { },

  updateEligibilityFlagsDynamic: async (_filters?: {
    registeredContestantId?: number;
    pageantAvailableTitleId?: number;
    pageantId?: number;
  }) => { },

  currentPageant: [],
  setCurrentPageant: () => { },

  supremeScoringSettings: { customRules: [], dropHighestScore: false, dropLowestScore: false },
  setSupremeScoringSettings: () => { },
  handleSupremeScoreSetupNew: () => { }, // Default to a no-op function
  saveSupremeScoreCriteriaToStore: async () => { },

  resetAwardScoringCriteriaDynamic: async () => null,
  resetAwardRelationshipScoresDynamic: async () => null,


  pageantAvailableAwards: [],
  setPageantAvailableAwards: () => { },
  refetchPageantAvailableAwards: async (_filters?: {
    pageantId?: number;
    titleId?: number;
    awardTypeId?: number;
    registeredPageantContestantId?: number;
    pageantAvailablePackageId?: number;
  }) => { },

  pageantAvailableAOC: [],
  setPageantAvailableAOC: () => { },
  refetchPageantAvailableAOC: async (_filters?: {
    pageantId?: number;
    aocId?: number;
    aocTypeId?: number;
    registeredPageantContestantId?: number;
    pageantAvailablePackageId?: number;
  }) => { },

  awardRelationships: [],
  setAwardRelationships: () => { },
  refetchAwardRelationships: async (_filters?: {
    registeredContestantId?: number;
    pageantAvailableTitleId?: number;
    pageantId?: number;
  }) => { },

  //award calculations
  runAwardRecalcSequence: async () => { /* no-op default */ },
  cancelAwardRecalcSequence: () => { /* no-op default */ },
  awardCalcProgress: {
    running: false,
    phase: 'idle',
    currentIndex: 0,
    totalAwards: 0,
    completed: 0,
    resultsByAwardId: {},
  },

  totalPointsPossible: {},
  setTotalPointsPossible: () => { },
  fetchAllStarted: false,
  setFetchAllStarted: () => { },
  isDataLoaded: false,
  setIsDataLoaded: () => { },
  isLoadingScoring: false,
  loadingText: undefined,
  showLoader: () => { },
  hideLoader: () => { },

  awardTypes: [],
  setAwardTypes: () => { },

  aocTypes: [],
  setAocTypes: () => { },


  existingAOCScoringCategories: {}, // Initialize as an empty object
  setExistingAOCScoringCategories: () => { }, // Default setter
  pageant: {},
  isScoringAndAdmin: false,
  calculateFinalScoreWithDrops: () => ({ finalScore: 0, droppedScores: [] }),
  calculateAOCFinalScoreWithDrops: () => ({ finalScore: 0, droppedScores: [] }),
  calculateAOCTotalScore: (aocId: number, judgeIndex: number, contestantId: number) => {
    console.warn("calculateAOCTotalScore not implemented"); // Log warning if function is called
    return 0; // Default return value
  },
  fetchTotalScoresWithDropsForContestantAsyncAll: async (contestantId: number): Promise<{ [key: string]: any }> => {
    // Default implementation (can be overridden later)
    return {}; // Return an empty object as default
  },

  fetchContestantCriteriaAndScoringCriteria: async () => null,

  // ✅ Add missing methods with empty default implementations
  // fetchAllScoreAndTallyData: async () => { },
  fetchAllScoreAndTallyData: async (pageantId: number) => { },
  orderPageantAgeGroupsYoungestToOldest: () => [],

  highlightAOCsUsedForMasterScoreNew: () => [],

  // customJudgeNames: {}, // Initialize as an empty object
  // setCustomJudgeNames: () => { },

  pageantStore: GetPageantStore(),
  eventStore: GetEventStore(),
  userStore: GetUserStore(),
  judgeStore: GetEventJudgeStore(),
  pageantRegistrationStore: GetPageantRegistrationStore(),
  awardStore: GetPageantAwardStore(),
  tabulationStore: GetTabulationStore(),
  aocStore: GetPageantAOCStore(),

  selectedContestant: null,
  setSelectedContestant: () => { },
  fetchContestantAttributes: () => { },






  //=====================AWARD CRITERIA====================
normalizeAwardCriteria: (criteria: any, pageant: any) => {
  const divisions = pageant?.eventDivisions ?? pageant?.pageantDivisions ?? [];

  const divisionNames =
    criteria.divisions?.length
      ? criteria.divisions
      : criteria.division?.length
        ? criteria.division
        : ["ALL"];

  return {
    ageGroups: criteria.ageGroups?.length ? criteria.ageGroups : ["ALL"],
    ageGroupIds: (criteria.ageGroups || [])
      .map((name: string) =>
        pageant?.pageantAgeGroups?.find((g: any) => getAgeGroupName(g) === name)?.id
      )
      .filter(Boolean) || [],

    supremeGroups: criteria.supremeGroups?.length ? criteria.supremeGroups : ["ALL"],
    supremeGroupIds: (criteria.supremeGroups || [])
      .map((name: string) =>
        pageant?.pageantAgeGroups?.find(
          (g: any) => getAgeGroupName(g) === name && g.isSupreme
        )?.id
      )
      .filter(Boolean) || [],

    division: divisionNames,
    divisions: divisionNames,
    divisionIds: divisionNames.includes("ALL")
      ? []
      : divisionNames
          .map((name: string) =>
            divisions.find((d: any) => String(d.name) === String(name))?.id
          )
          .filter(Boolean),

    levels: criteria.levels?.length ? criteria.levels : ["ALL"],
    levelIds: (criteria.levels || [])
      .map((name: string) =>
        pageant?.pageantLevelGroups?.find((l: any) => l.name === name)?.id
      )
      .filter(Boolean) || [],

    competeStyles: criteria.competeStyles?.length ? criteria.competeStyles : ["ALL"],
    competeStyleIds: (criteria.competeStyles || [])
      .map((name: string) =>
        optionsCompeteStyles.find((style) => style.label === name)?.value
      )
      .filter(Boolean) || [],

    genders: criteria.genders?.length ? criteria.genders : ["ALL"],
  };
},




  //==================== HELPERS ====================
  contestantBasicInfoForPageant: {}, // ✅ Store contestant names in a dictionary
  setContestantBasicInfoForPageant: () => { },
  fetchBasicContestantInfo: async (contestantId: number | null) => {
    if (!contestantId) {
      return {
        fullName: "N/A",
        contestantNumber: "N/A",
      };
    }
    return {
      fullName: "N/A",
      contestantNumber: "N/A",
    };
  },
  // fetchAllBasicContestantInfoForPageant: async () => { },
  fetchTitleDetails: async () => ({ name: "N/A", pullOrder: 0 }), // Placeholder

  //tabulation status
  tabulationStatus: null,
  //  refreshTabulationStatus: () => { },


};


// Use the default value in `createContext`
export const TabulationDataContext = createContext<TabulationDataContextType>(defaultTabulationDataContext);


export const TabulationDataProvider = ({ children }) => {

  //#region==============LOADER START==========================================================================================================================
  const [isLoadingScoring, setIsLoadingScoring] = useState(false);
  const [loadingText, setLoadingText] = useState<string | undefined>(undefined);

  const showLoader = useCallback((text: string) => {
    setLoadingText(text);
    setIsLoadingScoring(true);
  }, []);

  const hideLoader = useCallback(() => {
    setIsLoadingScoring(false);
    setLoadingText(undefined);
  }, []);


  //#endregion==============LOADER END============================================================================================================================

  const [fetchAllStarted, setFetchAllStarted] = useState(false); //Fetchng All checking
  const [isDataLoaded, setIsDataLoaded] = useState(false); // All Data is loaded check
  // const [aocs, setAocs] = useState<AOC[]>([]); //Areas of Competition
  const [personnel, setPersonnel] = useState<EventPersonnelModel[]>([]); //Personnel (old way of doing judges)
  const [judges, setJudges] = useState<EventJudgeSummaryDTO[]>([]); //Judges (new way of doing judges)
  const [ageGroupsOrdered, setAgeGroupsOrdered] = useState<PageantAgeGroupBaseModel[]>([]); //Age Groups ordered youngest to oldest
  const [supremeGroupsOrdered, setSupremeGroupsOrdered] = useState<PageantAgeGroupBaseModel[]>([]); //Supreme Groups ordered youngest to oldest

  const [currentPageant, setCurrentPageant] = useState<Pageant[]>([]); //RegisteredContestants

  const [registeredContestantsNew, setRegisteredContestantsNew] = useState<RegisteredContestant[]>([]); //RegisteredContestants

  const pendingContestantRefreshIdsRef = useRef<Set<number>>(new Set());
  const contestantRefreshTimeoutRef = useRef<number | null>(null);
  const [liveScoreNotifications, setLiveScoreNotifications] = useState<any[]>([]);



  const [pageantAvailableAwards, setPageantAvailableAwards] = useState<PageantAvailableAward[]>([]); //All Available Awards
  const [pageantAvailableAOC, setPageantAvailableAOC] = useState<PageantAvailableAOC[]>([]); //All Available AOC


  const [awardRelationships, setAwardRelationships] = useState<RegisteredContestantAwardRelationshipBaseModel[]>([]); //Award Relationships
  const [allFetchedScores, setAllFetchedScores] = useState<{ [contestantId: number]: ReceivedScoreBaseModel[] }>({}); //All scores


  const [allSupremeScores, setAllSupremeScores] = useState<{ [contestantId: number]: ContestantSupremeTotalScore }>({});

  const [tabulationStatus, setTabulationStatus] = useState<TabulationStatus | null>(null); // Tabulation Status testing



  const [scoresForAOCCompare, setScoresForAOCCompare] = useState<{ [contestantId: number]: { [aocId: number]: { [categoryName: string]: number[] } } }>({});
  const [selectedContestant, setSelectedContestant] = useState<RegisteredContestant | null>(null); //Selected Contestant
  const [existingAOCScoringCategories, setExistingAOCScoringCategories] = useState<{
    [aocId: number]: (AOCScoringCategoryModel & { scoringCategoryName?: string })[];
  }>({});

  const [totalPointsPossible, setTotalPointsPossible] = useState<{ [key: number]: number }>({}); //Total Points Possible

  const [aocTypes, setAocTypes] = useState<AreaOfCompetitionTypeBaseModel[]>([]); // Types of Areas of competition (stage, photo, etc)
  const [awardTypes, setAwardTypes] = useState<AwardTypeBaseModel[]>([]); // Types of Awards (title, side award, jackpot, etc)

  const [eventDivisions, setEventDivisions] = useState<EventDivisionBaseModel[]>([]);


  const [contestantBasicInfoForPageant, setContestantBasicInfoForPageant] = useState<{ [contestantId: number]: string }>({});
  const [uploadedScores, setUploadedScores] = useState<{
    [contestantId: string]: {
      [aocName: string]: {
        [categoryName: string]: {
          [judgeName: string]: number;
        };
      };
    };
  }>({});  // Uploaded scores from Excel
  const [supremeScoringSettings, setSupremeScoringSettings] = useState<ScoringCriteria>({
    customRules: [],
    dropHighestScore: false,
    dropLowestScore: false,
  }); // Supreme Settings

  const [sideAwardWinners, setSideAwardWinners] = useState<any[]>([]); // Side Award Winners
  const [jackpotWinners, setJackpotWinners] = useState<any[]>([]); // Jackpot Winners
  const [titleWinners, setTitleWinners] = useState<any[]>([]); // Title Winners

  const allFetchedScoresRef = useRef(allFetchedScores);
const pendingLiveNotificationKeysRef = useRef<Set<string>>(new Set());
const liveNotificationTimeoutRef = useRef<number | null>(null);

useEffect(() => {
  allFetchedScoresRef.current = allFetchedScores;
}, [allFetchedScores]);
  //=================NEW JUDGES AND AOC AND COUNTS - testing===================
  const getJudgeAocIds = (judge: any): number[] => {
    const areas =
      judge?.areasOfCompetition ??
      [];

    return areas
      .map((x: any) =>
        Number(
          x?.pageantAvailableEventId
        )
      )
      .filter((id: number) => Number.isFinite(id) && id > 0);
  };

  const getJudgesForAoc = (aocId: number) => {
    return (judges ?? []).filter((judge: any) => {
      const assignedAocIds = getJudgeAocIds(judge);

      // If no assignments are present, treat judge as eligible for all AOCs.
      // Remove this fallback if every judge should always have explicit AOC assignments.
      if (assignedAocIds.length === 0) return true;

      return assignedAocIds.includes(Number(aocId));
    });
  };

  const getJudgeCountForAoc = (aocId: number): number => {
    return getJudgesForAoc(aocId).length;
  };

  const getAocBaseMaxPoints = (aoc: PageantAvailableAOC): number =>
    (aoc.aocScoreCategories ?? []).reduce(
      (sum: number, cat: any) => sum + (Number(cat?.maxScore ?? 0) || 0),
      0
    );

  const getDropCount = (settings: ScoringCriteria, judgeCount: number): number => {
    let drops = 0;
    if (settings.dropLowestScore) drops += 1;
    if (settings.dropHighestScore) drops += 1;
    return Math.min(drops, Math.max(judgeCount - 1, 0));
  };

  const getAocMaxPoints = (
    aocId: number,
    settings: ScoringCriteria = supremeScoringSettings,
    withDrops = false
  ): number => {
    const aoc = pageantAvailableAOC.find(a => a.id === aocId);
    if (!aoc) return 0;

    const baseMax = getAocBaseMaxPoints(aoc);
    const judgeCount = withDrops
      ? getAdjustedJudgeCountForAoc(aocId, settings)
      : getJudgeCountForAoc(aocId);

    return baseMax * judgeCount;
  };



  //===============================AWARD SCORING PROCESS Start======================
  const [awardCalcProgress, setAwardCalcProgress] = React.useState<AwardCalcProgress>({
    running: false,
    phase: 'idle',
    currentIndex: 0,
    totalAwards: 0,
    completed: 0,
    resultsByAwardId: {},
  });
  const cancelAwardCalcRef = React.useRef(false);


  //===============================AWARD SCORING PROCESS End======================





  //#region=========================STORES START==============================================================================================
  const pageantStore = GetPageantStore();
  const awardStore = GetPageantAwardStore();
  const tabulationStore = GetTabulationStore();
  const aocStore = GetPageantAOCStore();
  const pageantRegistrationStore = GetPageantRegistrationStore();
  const pageant = pageantStore.CurrentPageant;
  const eventStore = GetEventStore();
  const userStore = GetUserStore();
  const judgeStore = GetEventJudgeStore();
  //#endregion=========================STORES END===========================================================================================


  const ensureCurrentPageant = async (pageantId?: number) => {
    const pid = pageantId ?? pageantStore.CurrentPageant?.id ?? 0;
    if (!pid) return null;

    if (pageantStore.CurrentPageant?.id !== pid) {
      await pageantStore.getPageant(pid); // load once
    }
    return pageantStore.CurrentPageant ?? null;
  };

  const ensureCurrentEvent = async (eventId?: number) => {
    const eid = eventId ?? eventStore.CurrentEvent?.id ?? 0;
    if (!eid) return null;

    if (eventStore.CurrentEvent?.id !== eid) {
      await eventStore.getEvent(eid); // load once
    }
    return eventStore.CurrentEvent ?? null;
  };

  const clearLiveScoreNotifications = useCallback(() => {
  setLiveScoreNotifications([]);
}, []);

  // const [customJudgeNames, setCustomJudgeNames] = useState({});

  // in TabulationDataProvider (or wherever pageant is finalized)
  useEffect(() => {
    primeCriteriaRegistryFromPageant(pageant);
  }, [pageant?.id, pageant?.pageantAgeGroups, pageant?.pageantLevelGroups]);

  // Load custom judge names from local storage when the provider is initialized
  // useEffect(() => {
  //   const savedData = JSON.parse(localStorage.getItem("customJudgeNames") || "{}");
  //   if (pageant.id && savedData[pageant.id]) {
  //     setCustomJudgeNames(savedData[pageant.id ?? 0]);
  //   }
  // }, [pageant.id ?? 0]);

  // // Save custom judge names to local storage
  // const saveCustomJudgeNames = () => {
  //   const savedData = JSON.parse(localStorage.getItem("customJudgeNames") || "{}");
  //   const pageantCustomNames = savedData[pageant.id ?? 0] || {};
  //   setCustomJudgeNames(pageantCustomNames);
  // };

  // // Helper to get judge name with fallback to default
  // const getJudgeName = (judgeIndex, aocId) => {
  //   const judgeNamesForAOC = customJudgeNames[aocId] || {};
  //   return judgeNamesForAOC[judgeIndex] || `Judge ${String.fromCharCode(65 + judgeIndex)}`; // Default to Judge A, B, etc.
  // };


  //#region============================VARIABLES START===============================================================================================

  const isScoringAndAdmin = pageant?.event?.isScoring && userStore?.CurrentUser?.roles?.includes("Admin");

  //#endregion=============================VARIABLES END===============================================================================================

  //#region===================LIVE SCORE SIGNAL R STUFF - testing START===================
 

  const eventId = Number(pageant?.event?.id ?? eventStore?.CurrentEvent?.id ?? 0);

const shouldUseLiveScores =
  Boolean(eventId) &&
  Boolean(isScoringAndAdmin) &&
  Array.isArray(judges) &&
  judges.length > 0;

useLiveScores({
  eventId,
  enabled: shouldUseLiveScores,

  onScoreSaved: async (savedScore: any) => {
    const contestantId = Number(
      savedScore.registeredPageantContestantId ??
        savedScore.registeredContestantId ??
        savedScore.contestantId
    );
    

    const judgeId = Number(savedScore.judgeId ?? savedScore.eventJudgeId);

    const aocId = Number(
      savedScore.pageantAvailableEventId ??
        savedScore.aocId
    );

    const categoryId = Number(
      savedScore.aocScoreCategoryId ??
        savedScore.aOCScoreCategoryId ??
        savedScore.aocScoringCategoryId
    );

    const scoreValue = Number(savedScore.score);

    console.log('[LiveScores] Score saved normalized', {
      contestantId,
      judgeId,
      aocId,
      categoryId,
      scoreValue,
      savedScore,
    });

    if (!contestantId || !judgeId || !aocId || !categoryId) {
      console.warn('[LiveScores] Missing required score IDs for progress update', {
        contestantId,
        judgeId,
        aocId,
        categoryId,
        savedScore,
      });
      return;
    }

    const contestant = registeredContestantsNew.find(
      (c: any) =>
        Number(c.id ?? c.registeredContestantId ?? c.registeredPageantContestantId) ===
        contestantId
    );

    const judge = judges.find((j: any) => Number(j.id) === judgeId);

    const aoc = pageantAvailableAOC.find((a: any) => Number(a.id) === aocId);

    const category = aoc?.aocScoreCategories?.find(
      (cat: any) => Number(cat.id) === categoryId
    );

    console.log('[LiveNotifications] matched live score entities', {
      contestant,
      judge,
      aoc,
      category,
    });

    setAllFetchedScores((prev) => {
      const existing = prev[contestantId] ?? [];

      const updated = existing.filter((s: any) => {
        const existingJudgeId = Number(s.judgeId ?? s.eventJudgeId);

        const existingAocId = Number(
          s.aocId ??
            s.pageantAvailableEventId
        );

     const existingCategoryId = Number(
  s.aocScoreCategoryId ??
    s.AOCScoreCategoryId ??
    s.AocScoreCategoryId ??
    s.aOCScoreCategoryId ??
    s.aocScoringCategoryId ??
    s.pageantAvailableAocScoreCategoryId ??
    0
);

        return !(
          existingJudgeId === judgeId &&
          existingAocId === aocId &&
          existingCategoryId === categoryId
        );
      });

      const normalizedScore = {
        ...savedScore,
        judgeId,
        eventJudgeId: judgeId,
        registeredPageantContestantId: contestantId,
        registeredContestantId: contestantId,
        pageantAvailableEventId: aocId,
        aocId,
        aocScoreCategoryId: categoryId,
        score: scoreValue,
        notes: savedScore.notes ?? '',
        dateReceived:
          savedScore.savedAt ??
          savedScore.dateReceived ??
          new Date().toISOString(),
      } as ReceivedScoreBaseModel;

      console.log('[LiveScores] updating allFetchedScores', {
        contestantId,
        previousCount: existing.length,
        updatedCount: updated.length + 1,
        normalizedScore,
      });

      return {
        ...prev,
        [contestantId]: [...updated, normalizedScore],
      };
    });

 pendingLiveNotificationKeysRef.current.add(`${judgeId}|${contestantId}|${aocId}`);

console.log('[LiveNotifications] queued grouped notification', {
  key: `${judgeId}|${contestantId}|${aocId}`,
  judgeId,
  contestantId,
  aocId,
  categoryId,
  scoreValue,
});

if (liveNotificationTimeoutRef.current) {
  window.clearTimeout(liveNotificationTimeoutRef.current);
}

liveNotificationTimeoutRef.current = window.setTimeout(() => {
  const keys = Array.from(pendingLiveNotificationKeysRef.current);

  console.log('[LiveNotifications] processing grouped notifications', {
    keys,
    allFetchedScoresForContestant: allFetchedScoresRef.current[contestantId],
  });

  keys.forEach((key) => {
    const [judgeIdRaw, contestantIdRaw, aocIdRaw] = key.split('|');

    const groupedJudgeId = Number(judgeIdRaw);
    const groupedContestantId = Number(contestantIdRaw);
    const groupedAocId = Number(aocIdRaw);

    const groupedContestant = registeredContestantsNew.find(
      (c: any) =>
        Number(c.id ?? c.registeredContestantId ?? c.registeredPageantContestantId) ===
        groupedContestantId
    );

    const groupedJudge = judges.find((j: any) => Number(j.id) === groupedJudgeId);

    const groupedAoc = pageantAvailableAOC.find(
      (a: any) => Number(a.id) === groupedAocId
    );

    if (!groupedContestant || !groupedJudge || !groupedAoc) {
      console.warn('[LiveNotifications] grouped notification skipped', {
        groupedJudgeId,
        groupedContestantId,
        groupedAocId,
        contestantFound: Boolean(groupedContestant),
        judgeFound: Boolean(groupedJudge),
        aocFound: Boolean(groupedAoc),
      });
      return;
    }

    const scoreRows =
      allFetchedScoresRef.current[groupedContestantId] ??
      allFetchedScoresRef.current[String(groupedContestantId) as any] ??
      [];

    const categoryScores = (groupedAoc.aocScoreCategories ?? []).map((category: any) => {
      const aocScoreCategoryId = Number(category.id);

      const row = scoreRows.find((scoreRow: any) => {
        const rowJudgeId = Number(scoreRow.judgeId ?? scoreRow.eventJudgeId);

        const rowAocId = Number(
          scoreRow.pageantAvailableEventId ??
            scoreRow.aocId
        );

        const rowAocScoreCategoryId = Number(
          scoreRow.aocScoreCategoryId ??
            scoreRow.AOCScoreCategoryId ??
            scoreRow.AocScoreCategoryId ??
            scoreRow.aOCScoreCategoryId ??
            scoreRow.aocScoringCategoryId ??
            scoreRow.pageantAvailableAocScoreCategoryId ??
            0
        );

        return (
          rowJudgeId === groupedJudgeId &&
          rowAocId === groupedAocId &&
          rowAocScoreCategoryId === aocScoreCategoryId
        );
      });

      const rawScore = row?.score ?? row?.score ?? null;
      const score =
        rawScore === null || rawScore === undefined 
          ? null
          : Number(rawScore);

      return {
        aocScoreCategoryId,
        scoringCategoryId: Number(category.scoringCategoryId ?? 0) || undefined,
        categoryName:
          category.scoringCategoryName ??
          category.name ??
          category.categoryName ??
          `Category ${aocScoreCategoryId}`,
        score: Number.isFinite(score) ? score : null,
        isMissing: !Number.isFinite(score),
      };
    });

    console.log('[LiveNotifications] grouped category scores built', {
      groupedJudgeId,
      groupedContestantId,
      groupedAocId,
      categoryScores,
      scoreRows,
    });

    const judgeAocTotal = categoryScores.reduce(
      (sum, category) => sum + (category.score ?? 0),
      0
    );

    const hasMissingCategory = categoryScores.some((category) => category.isMissing);

    setLiveScoreNotifications((prev) => [
      {
        id: `score-saved-${groupedJudgeId}-${groupedContestantId}-${groupedAocId}-${Date.now()}`,
        type: hasMissingCategory ? 'missing-score' : 'score-saved',

        judgeId: groupedJudgeId,
        judgeLabel: groupedJudge.judgeLabel,
        judgeName: groupedJudge.judgeName,

        contestantId: groupedContestantId,
        contestantNumber: groupedContestant.contestantNumber,
        contestantName:
          groupedContestant.fullName ||
          `${groupedContestant.firstName ?? ''} ${groupedContestant.lastName ?? ''}`.trim() ||
          'Unknown Contestant',

        aocId: groupedAocId,
        aocName: groupedAoc.name ?? `AOC ${groupedAocId}`,

        categoryScores,
        judgeAocTotal,
        savedAt: new Date().toISOString(),
      },
      ...prev,
    ].slice(0, 50));
  });

  pendingLiveNotificationKeysRef.current.clear();
}, 750);

    setJudges((prev) =>
      prev.map((j: any) => {
        if (Number(j.id) !== judgeId) return j;

        return {
          ...j,
          liveStatus: 'Online',
          lastSeenAt: savedScore.savedAt ?? new Date().toISOString(),
          currentScoringContext: {
            pageantAvailableEventId: aocId,
            aocName: aoc?.name ?? 'Unknown AOC',
            divisionName: contestant?.attributes?.eventDivision ?? '',
            ageGroupName: contestant?.attributes?.ageGroup ?? '',
            registeredContestantId: contestantId,
            contestantName:
              contestant?.fullName ||
              `${contestant?.firstName ?? ''} ${contestant?.lastName ?? ''}`.trim() ||
              'Unknown Contestant',
            contestantNumber: contestant?.contestantNumber ?? '',
            updatedAt: savedScore.savedAt ?? new Date().toISOString(),
          },
        };
      })
    );

   await refetchContestantScoresForLiveUpdate(contestantId);
queueContestantLiveRefresh(contestantId);
  },

  onJudgeHelpMessageSent: (payload) => {
  setLiveScoreNotifications((prev) => [
    {
      id: `judge-help-${payload.judgeId}-${Date.now()}`,
      type: 'judge-help-message',
      judgeId: Number(payload.judgeId),
      judgeLabel: payload.judgeLabel,
      judgeName: payload.judgeName,
      message: payload.message,
      savedAt: payload.sentAt ?? new Date().toISOString(),
    },
    ...prev,
  ].slice(0, 50));
},

//   onScoreDeletedOrg: async (deletedScore: any) => {
//   const contestantId = Number(
//     deletedScore.registeredPageantContestantId ??
//       deletedScore.registeredContestantId ??
//       deletedScore.contestantId
//   );

//   const judgeId = Number(deletedScore.judgeId ?? deletedScore.eventJudgeId);

//   const aocId = Number(
//     deletedScore.pageantAvailableEventId ??
//       deletedScore.aocId
//   );

//   const judge = judges.find((j: any) => Number(j.id) === judgeId);

//   const contestant = registeredContestantsNew.find(
//     (c: any) =>
//       Number(c.id ?? c.registeredContestantId ?? c.registeredPageantContestantId) ===
//       contestantId
//   );

//   const aoc = pageantAvailableAOC.find((a: any) => Number(a.id) === aocId);

//   setLiveScoreNotifications((prev) => [
//     {
//       id: `score-deleted-${judgeId}-${contestantId}-${aocId}-${Date.now()}`,
//       type: 'score-deleted',

//       judgeId,
//       judgeLabel:
//         deletedScore.judgeLabel ??
//         judge?.judgeLabel,
//       judgeName:
//         deletedScore.judgeName ??
//         judge?.judgeName,

//       contestantId,
//       contestantNumber:
//         deletedScore.contestantNumber ??
//         contestant?.contestantNumber,
//       contestantName:
//         deletedScore.contestantName ??
//         contestant?.fullName ??
//         `${contestant?.firstName ?? ''} ${contestant?.lastName ?? ''}`.trim() ??
//         'Unknown Contestant',

//       aocId,
//       aocName:
//         deletedScore.aocName ??
//         aoc?.name ??
//         `AOC ${aocId}`,

//       categoryScores: [],
//       judgeAocTotal: null,
//       savedAt: deletedScore.deletedAt ?? new Date().toISOString(),
//     },
//     ...prev,
//   ].slice(0, 50));

// const updatedScores = await refetchContestantScoresForLiveUpdate(contestantId);

// await fetchRegisteredContestantsDynamic(
//   { registeredContestantIds: [contestantId] },
//   {
//     replaceAll: false,
//     silent: true,
//     scoresByContestantOverride: {
//       ...allFetchedScoresRef.current,
//       [contestantId]: updatedScores,
//     },
//   },
// );
// },
  
onScoreDeleted: async (deletedScore: any) => {
  const contestantId = Number(
    deletedScore.registeredPageantContestantId ??
      deletedScore.registeredContestantId ??
      deletedScore.contestantId
  );

  const judgeId = Number(deletedScore.judgeId ?? deletedScore.eventJudgeId);

  const aocId = Number(
    deletedScore.pageantAvailableEventId ??
      deletedScore.aocId
  );

  const judge = judges.find((j: any) => Number(j.id) === judgeId);

  const contestant = registeredContestantsNew.find(
    (c: any) =>
      Number(c.id ?? c.registeredContestantId ?? c.registeredPageantContestantId) === contestantId
  );

  const aoc = pageantAvailableAOC.find((a: any) => Number(a.id) === aocId);

  setLiveScoreNotifications((prev) => [
    {
      id: `score-deleted-${judgeId}-${contestantId}-${aocId}-${Date.now()}`,
      type: "score-deleted",

      judgeId,
      judgeLabel: deletedScore.judgeLabel ?? judge?.judgeLabel,
      judgeName: deletedScore.judgeName ?? judge?.judgeName,

      contestantId,
      contestantNumber: deletedScore.contestantNumber ?? contestant?.contestantNumber,
      contestantName:
        deletedScore.contestantName ??
        contestant?.fullName ??
        `${contestant?.firstName ?? ""} ${contestant?.lastName ?? ""}`.trim() ??
        "Unknown Contestant",

      aocId,
      aocName: deletedScore.aocName ?? aoc?.name ?? `AOC ${aocId}`,

      categoryScores: [],
      judgeAocTotal: null,
      savedAt: deletedScore.deletedAt ?? new Date().toISOString(),
    },
    ...prev,
  ].slice(0, 50));

  const updatedScores =
    (await refetchContestantScoresForLiveUpdate(contestantId)) ?? [];

  const nextScoresByContestant = {
    ...allFetchedScoresRef.current,
    [contestantId]: updatedScores,
  };

  allFetchedScoresRef.current = nextScoresByContestant;
  setAllFetchedScores(nextScoresByContestant);

  await fetchRegisteredContestantsDynamic(
    { registeredContestantIds: [contestantId] },
    {
      replaceAll: false,
      silent: true,
      scoresByContestantOverride: nextScoresByContestant,
    }
  );
},

onJudgeStatusChanged: (status) => {
  console.log('[LiveScores] JudgeStatusChanged normalized', status);

  setJudges((prev) =>
    prev.map((j: any) =>
      Number(j.id) === Number(status.judgeId)
        ? {
            ...j,
            // Live websocket status: Online / Offline / Scoring
            liveStatus: status.liveStatus ?? status.status ?? 'Online',

            // Admin status: Active / Inactive
            status: status.status ?? j.status,

            isActive: status.status === 'Active',
            isOnline: status.liveStatus === 'Online',

            lastSeenAt: status.lastSeenAt,
            lastLoginAt: j.lastLoginAt ?? status.lastSeenAt,
          }
        : j,
    ),
  );
},
});

  //#endregion===================LIVE SCORE SIGNAL R STUFF - testing END===================

  // #region ============================================SAVES START================================================
  // TabulationDataProvider.tsx (inside the provider component)

  // expose this via context if not already:
  const saveSupremeScoreCriteriaToStore = async (
    pageantId: number,
    criteria: ScoringCriteria,
    opts?: {
      updateAwardsUsingSupreme?: boolean;
      resetAwardsUsingSupreme?: boolean;
      calculateScores?: boolean;
    }
  ) => {
    showLoader('Saving Supreme Scoring Criteria…');
    try {
      // 1) Save Supreme settings
      await tabulationStore.saveSupremeScoringCriteria(pageantId, criteria);
      setSupremeScoringSettings(criteria);

      // find all awards that currently "Use Supreme Scoring"
      const idsUsingSupreme =
        (pageantAvailableAwards ?? [])
          .filter(a => !!a?.specificAwardFullCriteria?.additionalScoringDetails?.isUseSupremeScoreSettings)
          .map(a => a.id);

      // 2a) UPDATE those awards to the NEW Supreme settings
      if (opts?.updateAwardsUsingSupreme && idsUsingSupreme.length) {
        showLoader('Updating awards that inherit Supreme scoring…');
        for (const awardId of idsUsingSupreme) {
          await tabulationStore.saveAwardScoringCriteria(awardId, criteria);
          await refetchPageantAvailableAwards({ titleId: awardId });
        }
      }

      // 2b) Or RESET those awards (clear award-level criteria + their relationship scores)
      if (opts?.resetAwardsUsingSupreme && idsUsingSupreme.length) {
        showLoader('Resetting awards that inherit Supreme scoring…');
        await resetAwardScoringCriteriaDynamic({ pageantAvailableTitleIds: idsUsingSupreme });
        await refetchPageantAvailableAwards({ pageantId: pageantId });
      }

      // 3) Optionally calculate all contestants’ Supreme scores
      if (opts?.calculateScores) {
        showLoader('Calculating and saving contestants’ Supreme scores…');
        await tabulationStore.calculateAndSaveAllSupremeScoresInPageant(pageantId);

      }

      // 4) Refresh contestants so UI reflects changes
      await refetchRegisteredContestants();
    } finally {
      hideLoader();
    }
  };

  // #endregion ============================================SAVES END================================================



  //#region ==========================================FETCH TABULATION SUMMARIES DYNAMICALLY START - testing==================================
  // ================== Dynamic mapping & fetch ==================
  // Normalize API -> UI attributes
  // Overloads so TS accepts both API and UI shapes
  function mapContestantAttributes(
    api: ContestantAttributesModel | undefined,
    ctx: { contestantId: number; base?: any; sourcePageant?: any }
  ): ContestantAttributes;
  function mapContestantAttributes(
    api: ContestantAttributes | undefined,
    ctx: { contestantId: number; base?: any; sourcePageant?: any }
  ): ContestantAttributes;

  // Implementation
  function mapContestantAttributes(
    api: any,
    {
      contestantId,
      base,
      sourcePageant,
    }: { contestantId: number; base?: any; sourcePageant?: any }
  ): ContestantAttributes {
    const ageGroupId =
      api?.ageGroupId ?? base?.ageGroupId ?? 0;

    const ageGroupNameFromBase =
      base?.pageantAgeGroup ? getAgeGroupName(base.pageantAgeGroup) : undefined;

    const ageGroupNameFromApi =
      api?.ageGroupName ?? api?.ageGroup;

    const levelId =
      api?.levelId ?? base?.pageantLevelId ?? 0;

    const levelNameFromApi =
      api?.levelName ?? api?.level;

    const levelNameFromLookup =
      sourcePageant?.pageantLevelGroups?.find((x: any) => x.id === levelId)?.name;

    const genderId =
      api?.genderId ?? base?.contestantUser?.genderId ?? 0;

    const genderFromApi =
      api?.gender ?? api?.genderName;

    const competeStyleId =
      api?.competeStyleId ??
      api?.competeStyleID ??
      api?.competeStyle ??
      base?.competeStyleId ??
      0;

    const competeStyleLabel =
      competeStyleId
        ? (optionsCompeteStyles.find(o => o.value === competeStyleId)?.label ?? '')
        : (api?.competeStyleName ?? api?.competeStyleLabel ?? '');

    const eventDivisionId =
      api?.eventDivisionId ?? base?.eventDivisionId ?? 0;

    const eventDivisionNameFromApi =
      api?.eventDivision ?? api?.eventDivisionName ?? '';

    // supreme groups can be string[] or string; keep both forms available
    const supremeGroupIds: number[] =
      api?.supremeGroupIds ??
      (Array.isArray(api?.supremeGroups) ? api.supremeGroups : []) ?? // if API sometimes sends ids in this field
      [];

    const supremeGroupsJoined: string =
      Array.isArray(api?.supremeGroups)
        ? api.supremeGroups.join(', ')
        : (api?.supremeGroups ??
          api?.supremeGroupNames ??
          '');

    // NEW: fallback/move-up fields; prefer API, then base (if present)
    const contestantAgeDayOfPageant =
      api?.contestantAgeDayOfPageant ?? base?.contestantAgeDayOfPageant ?? null;

    const contestantAgeDayOfFallback =
      api?.contestantAgeDayOfFallback ?? base?.contestantAgeDayOfFallback ?? null;

    const startAgeOfContestantAgeGroup =
      api?.startAgeOfContestantAgeGroup ?? base?.startAgeOfContestantAgeGroup ?? null;

    const contestantTookFallback: boolean | null =
      (api?.contestantTookFallback ?? base?.contestantTookFallback) ?? null;

    const contestantMovedUpAgeGroup: boolean | null =
      (api?.contestantMovedUpAgeGroup ?? base?.contestantMovedUpAgeGroup) ?? null;

    const contestantAgeUsedForSupremeGroup =
      api?.contestantAgeUsedForSupremeGroup ?? base?.contestantAgeUsedForSupremeGroup ?? null;

    return {
      id: contestantId,

      ageGroupId,
      ageGroup:
        (ageGroupNameFromApi as string | undefined) ??
        ageGroupNameFromBase ??
        'N/A',

      supremeGroups: supremeGroupsJoined, // ContestantAttributes should allow string | string[]
      supremeGroupIds,

      level:
        (levelNameFromApi as string | undefined) ??
        levelNameFromLookup ??
        '',

      levelId,

      gender:
        (genderFromApi as string | undefined) ??
        (genderId === 1 ? 'Female' : genderId === 2 ? 'Male' : ''),

      genderId,

      competeStyleId,
      competeStyle: competeStyleLabel,

      eventDivisionId,
      eventDivision: eventDivisionNameFromApi || '',

      // NEW fields exposed to the UI
      contestantAgeDayOfPageant,
      contestantAgeDayOfFallback,
      startAgeOfContestantAgeGroup,
      contestantTookFallback,
      contestantMovedUpAgeGroup,
      contestantAgeUsedForSupremeGroup,
    };
  }

  // --- helpers (put near the top of the file) ---
  const nearlyEqual = (a?: number, b?: number, eps = 1e-6) =>
    a != null && b != null && Math.abs(a - b) < eps;

  // Takes a dynamic summary row and builds your RegisteredContestant model
  const mapSummaryToRegisteredContestant = (
    s: ContestantComprehensiveTabulationResultNew,
    sourcePageant: any,
    allScoresByContestant: { [contestantId: number]: ReceivedScoreBaseModel[] }
  ): RegisteredContestant => {
  const contestantId = Number(s.registeredContestantId ?? 0);

const normalizedScoresByContestant =
  normalizeScoresByContestant(allScoresByContestant);

const contestantScores: ReceivedScoreBaseModel[] =
  normalizedScoresByContestant[contestantId] ?? [];
  

    // pull base contestant record from the current pageant to get static/profile bits
    const base = sourcePageant?.registeredPageantContestants?.find((c: any) => c.id === contestantId);
    const contestantScoresRaw =
  allScoresByContestant?.[Number(contestantId)] ?? [];




    const normalizeStatusInfoArray = (raw: any): StatusDetails[] | undefined => {
      if (!raw) return undefined;
      const arr = Array.isArray(raw) ? raw : [raw];
      const out = arr.map((si: any) => ({
        status: si?.status ?? null,
        badgeColor: si?.badgeColor ?? null,
        details: (si?.details ?? si?.statusDetails) ?? null,
        potentialErrors: si?.potentialErrors ?? null,
      }));
      return out.length ? out : undefined;
    };

    // AOCs (already detailed in the summary)
    const aocs: ContestantAOCSDetails[] = (s.contestantAreaOfCompetitionDetails ?? [])
      .map((aoc: any) => {

        return {
          aocId: aoc.aocId ?? 0,
          aocTypeId: aoc.aocTypeId ?? 0,
          aocTypeName: aoc.aocTypeName ?? '',
          aocName: aoc.aocName ?? 'Unknown',
          competeOrder: aoc.competeOrder ?? Number.MAX_SAFE_INTEGER,

          aocTotalScore: aoc.aocTotalScore ?? 0,
          aocTotalScoreWithDrops: aoc.aocFinalScoreWithDrops ?? 0,
          aocMaxPossiblePoints: aoc.totalMaxPointsPossible ?? 0,
          aocMaxPossiblePointsWithDrops: aoc.finalMaxPointsPossibleWithDrops ?? 0,


          aocUsedPlusMarkCount: aoc.aocUsedPlusMarkCount,

          entrySource: aoc.entrySource ?? 'Unknown',
          aocComplete: aoc.aocCompleted ?? false,
          isEntered: aoc.isEntered ?? false,
          isUsedTowardSupremeScore: aoc.usedForSupremeScore ?? false,
          status: aoc.status ?? 'Unknown',
          statusDetails: aoc.statusDetails ?? '',

          statusInfo: normalizeStatusInfoArray(aoc.statusInfo),

          judgeScores: (aoc.judgeScores ?? []).map((js: any) => ({
            judgeId: js.judgeId,
            judgeName: js.judgeName,
            totalAOCScoreFromThisJudge: js.totalAOCScoreFromThisJudge ?? 0,
            isDropped: !!js.isDropped, // 👈 server flag used to exclude from plus marks
            judgeAOCPlusMarkCount: js.judgeAOCPlusMarkCount,
            categoryScores: (js.scoringCategories ?? []).map((cs: any) => ({
              aocScoringCategoryId: cs.aocScoreCategoryId,
              scoringCategoryId: cs.scoringCategoryId,
              scoringCategoryName: cs.scoringCategoryName ?? 'Unknown',
              score: cs.score ?? null,
              notes: cs.notes ?? null,
              categoryPlusMarkCount: cs.categoryPlusMarkCount,
              maxPossiblePointsPerJudge: cs.maxPossiblePointsPerJudge ?? 0,
            })),
          })),
        } as ContestantAOCSDetails;
      })
      .sort((a, b) => {
        if (a.aocTypeId !== b.aocTypeId) return a.aocTypeId - b.aocTypeId;
        if ((a.competeOrder ?? 0) !== (b.competeOrder ?? 0)) return (a.competeOrder ?? 0) - (b.competeOrder ?? 0);
        return (a.aocName ?? '').localeCompare(b.aocName ?? '');
      });

    // Awards (already detailed in the summary)
    const awards: ContestantAwardDetails[] = (s.contestantAwardDetails ?? [])
      .map((aw: any) => ({
        awardId: aw.awardId ?? 0,
        awardName: aw.awardName ?? 'Unknown',
        awardTypeId: aw.awardTypeId ?? 0,
        awardTypeName: aw.awardTypeName ?? 'Unknown',
        pullOrder: aw.pullOrder ?? 0,
        doubleCrown: aw.doubleCrown ?? null,
        useSupremeScoringCriteria: aw.useSupremeScoringCriteria ?? null,
        numberAvailable: aw.numberAvailable ?? 1,
        awardScoringCriteriaSettings: aw.awardScoringCriteria, // if your API returns it
        awardScoringCriteria: aw.awardScoringCriteriaBreakdown ?? '',
        aocCalculationComplete: aw.awardScoreCalculated ?? false,
        awardTotalCalculatedScore: aw.awardTotalCalculatedScore ?? 0,
        maxPointsPossible: aw.maxPossiblePointsAvailable ?? 0,
        entrySource: aw.entrySource ?? 'Unknown',
        isEntered: aw.isEntered ?? false,
        totalPlusMarkCountInAwardScore: aw.totalPlusMarkCountInAwardScore,
        statusInfo: normalizeStatusInfoArray(aw.statusInfo), // NEW
        selectedAOC: (aw.selectedAOCs ?? []).map((aoc: any) => ({
          aocId: aoc.aocId ?? 0,
          aocTypeId: aoc.aocTypeId ?? 0,
          aocName: aoc.aocName ?? 'Unknown AOC',
          aocScoreUsed: aoc.aocScoreUsed ?? 0,
          maxPossiblePointsAvailable: aoc.maxPossiblePointsAvailable ?? 0,
          usedSpecificScoringCategoryIds: aoc.usedSpecificScoringCategoryIds ?? null,
          aocUsedPlusMarkCount: aoc.aocUsedPlusMarkCount ?? null,
          judgeScores: (aoc.judgeScores ?? []).map((j: any) => ({
            judgeId: j.judgeId ?? 0,
            judgeName: j.judgeName ?? null,
            totalAOCScoreFromThisJudge: j.totalAOCScoreFromThisJudge ?? 0,
            isDropped: !!j.isDropped,
            judgeAOCPlusMarkCount: j.judgeAOCPlusMarkCount ?? null,
            scoringCategories: (j.scoringCategories ?? []).map((cat: any) => ({
              aocScoreCategoryId: cat.aocScoreCategoryId ?? 0,
              scoringCategoryName: cat.scoringCategoryName,
              scoringCategoryId: cat.scoringCategoryId ?? 0,
              score: cat.score ?? null,
              categoryPlusMarkCount: cat.categoryPlusMarkCount ?? null,
              maxPossiblePointsPerJudge: cat.maxPossiblePointsPerJudge ?? 0,
            })),
          })),
        })),
        individualAvailableAwardsDetails: (aw.individuallyAvailableAwardBreakdown ?? []).map((det: any) => ({
          awardIndex: det.awardIndex ?? 0,
          contestantCriteria: det.contestantCriteria ?? '',
          criteriaUIString:
  formatContestantCriteria(det.contestantCriteria) ||
  det.criteriaUIString ||
  '',
          isSavedAsEntered: det.isSavedAsEntered ?? false,
          meetsContestantCriteria: det.meetsContestantCriteria ?? false,
          isFullyEligible: det.isFullEligible ?? false,
          statusDetails: det.statusDetails ?? '',
          awardTotalCalculatedScore: det.awardTotalCalculatedScore ?? 0,
          isWon: det.isWon ?? false,
        })),
      }))
      .sort((a, b) => a.awardTypeId - b.awardTypeId);

    // Won awards summary
    const winnings: ContestantWonAward[] = awards
      .filter(aw => aw.individualAvailableAwardsDetails?.some(d => d.isWon))
      .map(aw => ({
        awardId: aw.awardId,
        awardName: aw.awardName,
        awardTypeId: aw.awardTypeId,
        awardTypeName: aw.awardTypeName,
        winningScore: aw.awardTotalCalculatedScore ?? 0,
        auditNote: aw.auditNote,
      }))
      .sort((a, b) => a.awardTypeId - b.awardTypeId);

    // Competition status rollup
    const enteredAOCs = aocs.filter(d => d.isEntered);
    // helpers (inline so you can paste as-is)
    // handles array | object | legacy shapes
    const readStatusText = (x: any): string => {
      const si = Array.isArray(x?.statusInfo)
        ? (x.statusInfo[0] ?? null)
        : x?.statusInfo ?? null;

      const s =
        si?.status ??
        si?.Status ??
        x?.StatusInfo?.Status ??
        x?.status ??
        x?.Status ??
        "";

      return (s ?? "").toString();
    };


    const CALC_COMPLETE = [
      /^Calculated\b/i,
      /^Calculated - Bonus Points$/i,
      /^Low Calculation$/i,
      /^Scored\b/i, // legacy safety
    ];
    const CALC_PROGRESS = [
      /^READY TO CALCULATE$/i,
      /^Entered\b/i,
      /^Entered - Not Calculated$/i,
      /^Entered - Not Scored$/i, // legacy safety
    ];
    const CALC_INCOMPLETE = [/^NOT READY$/i];

    const classifyAward = (aw: any): "complete" | "progress" | "incomplete" | "other" => {
      const t = readStatusText(aw);
      if (CALC_COMPLETE.some((re) => re.test(t))) return "complete";
      if (CALC_PROGRESS.some((re) => re.test(t))) return "progress";
      if (CALC_INCOMPLETE.some((re) => re.test(t))) return "incomplete";

      // fallback to your old booleans if present
      if (aw.aocCalculationComplete && aw.awardTotalCalculatedScore != null) return "complete";
      return "other";
    };

    // normalize AOC completed flag (your table used d.aocComplete)
    const isAocCompleted = (d: any) =>
      (typeof d?.aocComplete !== "undefined" ? !!d.aocComplete : !!(d?.aocCompleted ?? d?.AOCCompleted));

const compStatus = (() => {
  // --- AOCs ---
  const enteredAOCsTotal = enteredAOCs.length;
  const enteredAOCsCompleted = enteredAOCs.filter(isAocCompleted).length;
  const hasIncompleteEnteredAOCs = enteredAOCs.some((d) => !isAocCompleted(d));

  // --- Awards rollup ---
  const m = new Map<
    number,
    {
      name: string;
      awardCount: number;
      enteredCount: number;
      scoredCount: number;
      enteredScoredCount: number;
    }
  >();

  const enteredAwards: any[] = [];

  for (const aw of awards as any[]) {
    const awardTypeId = Number(aw.awardTypeId ?? 0);
    const awardTypeName = aw.awardTypeName ?? "Awards";
    const isEntered = !!aw.isEntered;
    const cls = classifyAward(aw);

    if (isEntered) enteredAwards.push(aw);

    const agg =
      m.get(awardTypeId) ??
      {
        name: awardTypeName,
        awardCount: 0,
        enteredCount: 0,
        scoredCount: 0,
        enteredScoredCount: 0,
      };

    agg.awardCount += 1;

    if (isEntered) {
      agg.enteredCount += 1;
    }

    // All calculated awards, entered or not
    if (cls === "complete") {
      agg.scoredCount += 1;
    }

    // Only calculated awards that contestant is entered in
    if (isEntered && cls === "complete") {
      agg.enteredScoredCount += 1;
    }

    m.set(awardTypeId, agg);
  }

  const awardsCalculationStatus = Array.from(m.entries()).map(([awardTypeId, v]) => ({
    awardTypeId,
    awardTypeName: v.name,
    awardCount: v.awardCount,
    enteredCount: v.enteredCount,
    scoredCount: v.scoredCount,
    enteredScoredCount: v.enteredScoredCount,

    // Use this for contestant live progress table
    isEnteredAwardsCalculated: v.enteredScoredCount === v.enteredCount,

    // Use this only when you truly need every award of that type calculated
    isAllAwardsCalculated: v.scoredCount === v.awardCount,
  }));

  const totals = awardsCalculationStatus.reduce(
    (t, x) => ({
      awardCount: t.awardCount + x.awardCount,
      enteredCount: t.enteredCount + x.enteredCount,
      scoredCount: t.scoredCount + x.scoredCount,
      enteredScoredCount: t.enteredScoredCount + x.enteredScoredCount,
    }),
    { awardCount: 0, enteredCount: 0, scoredCount: 0, enteredScoredCount: 0 }
  );

  const anyNotReadyAward = enteredAwards.some((aw) => classifyAward(aw) === "incomplete");

  const allEnteredAwardsCalcOrReady =
    enteredAwards.length === 0 ||
    enteredAwards.every((aw) => ["complete", "progress"].includes(classifyAward(aw)));

  const isReadyToScore =
    !hasIncompleteEnteredAOCs &&
    !anyNotReadyAward &&
    allEnteredAwardsCalcOrReady;

  return {
    enteredAOCsTotal,
    enteredAOCsCompleted,
    hasIncompleteEnteredAOCs,
    isReadyToScore,

    competitionStatus: s.competitionStatus ?? "",
    competitionStatusDetails: s.competitionStatusDetails ?? "",

    awardsCalculationStatus,
    totals,
  };
})();


    // Supreme details from summary
    const highlighted = (aocs ?? [])
      .filter(x => x.isUsedTowardSupremeScore)
      .map(x => ({
        aocId: x.aocId,
        aocName: x.aocName,
        finalScore: x.aocTotalScoreWithDrops ?? 0,
      }));

    const attributes: ContestantAttributes = mapContestantAttributes(
      s.contestantAttributes, // <-- API model
      { contestantId, base, sourcePageant }
    );

    // --- Registration invoice (resilient mapping) ---
    const invoicesRaw = s?.registrationInvoice
      ? (Array.isArray(s.registrationInvoice) ? s.registrationInvoice : [s.registrationInvoice])
      : [];

    const toNum = (v: any, d = 0) => {
      const n = typeof v === "string" ? parseFloat(v) : Number(v);
      return Number.isFinite(n) ? n : d;
    };

    const registrationInvoice: RegistrationInvoice[] = invoicesRaw.map((inv: any) => {
      const subtotal = toNum(inv?.subtotal);
      const discount = toNum(inv?.totalDiscount);
      const paid = toNum(inv?.amountPaid ?? inv?.paid);

      const balanceDue =
        inv?.balanceDue != null
          ? toNum(inv.balanceDue)
          : Math.max(0, subtotal - discount - paid);

      return {
        // include ids if your type has them
        // id: inv?.id ?? inv?.invoiceId,

        selectedPackages: Array.isArray(inv?.selectedPackages) ? inv.selectedPackages : [],
        otherFees: Array.isArray(inv?.otherFees) ? inv.otherFees : [],
        selectedAOCs: Array.isArray(inv?.selectedAOCs) ? inv.selectedAOCs : [],
        selectedAwards: Array.isArray(inv?.selectedAwards) ? inv.selectedAwards : [],

        subtotal,
        totalDiscount: discount,
        amountPaid: paid,              // ✅ fix the typo here
        balanceDue,
        status: inv?.status ?? "Unknown",
      };
    });


    return {
      id: contestantId,
      contestantNumber: base?.contestantNumber ?? '',
      firstName: base?.contestantUser?.firstName ?? '',
      lastName: base?.contestantUser?.lastName ?? '',
      fullName: `${base?.contestantUser?.firstName ?? ''} ${base?.contestantUser?.lastName ?? ''}`.trim(),
      headshot: base?.headShot?.image || base?.contestantUser?.profilePic?.image || undefined,

      selectedPackageIds: base?.registeredContestantSelectedPackages?.map((x: any) => x.pageantAvailablePackageId ?? 0) ?? [],
      selectedTitleIds: base?.registeredContestantSelectedTitles?.map((x: any) => x.pageantAvailableTitleId ?? 0) ?? [],
      selectedEventIds: base?.registeredContestantSelectedEvents?.map((x: any) => x.pageantAvailableEventId ?? 0) ?? [],
      mandatoryPackageIds: sourcePageant?.pageantAvailablePackages?.filter((x: any) => x.isMandatory && !x.isHiddenFromEventDetails).map((x: any) => x.id ?? 0) ?? [],

      contestantAreasOfCompetitionDetails: aocs,
      contestantAwardDetails: awards,
      attributes,

      allScores: contestantScores,
      grandTotalScore: s.grandTotal ?? 0,
      supremeScore: {
        contestantId,
        calculatedScore: s?.contestantSupremeScoreResult?.calculatedSupremeScore ?? 0,
        highlightedAocCount: highlighted.length,
        highlightedAocs: highlighted,
        // isTie: s?.contestantSupremeScoreResult?.isTie ?? false,
      },

      winnings,
      competitionStatus: {
        enteredAOCsTotal: compStatus.enteredAOCsTotal,
        enteredAOCsCompleted: compStatus.enteredAOCsCompleted,
        hasIncompleteEnteredAOCs: compStatus.hasIncompleteEnteredAOCs,
        isReadyToScore: compStatus.isReadyToScore,
        competitionStatus: s.competitionStatus ?? '',
        competitionStatusDetails: s.competitionStatusDetails ?? '',
        awardsCalculationStatus: compStatus.awardsCalculationStatus ?? [],
        totals: compStatus.totals ?? { awardCount: 0, enteredCount: 0, scoredCount: 0 },
      },

      registrationInvoice,
      // keep any other fields you maintain locally
    };
  };

  // Fully replace or partially patch existing state from dynamic summaries
  // keep this ref in your provider/component scope
  const rcSeq = React.useRef(0);

  const [registeredContestantsStatus, setRegisteredContestantsStatus] =
    React.useState<'idle' | 'loading' | 'success' | 'error'>('idle');




const fetchRegisteredContestantsDynamic = async (
  params: { pageantId?: number; registeredContestantIds?: number[] },
  opts?: {
    replaceAll?: boolean;
    silent?: boolean;
    scoresByContestantOverride?: Record<number, ReceivedScoreBaseModel[]>;
  }
): Promise<RegisteredContestant[]> => {
    const seq = ++rcSeq.current;

const scoresByContestant = normalizeScoresByContestant(
  opts?.scoresByContestantOverride ?? allFetchedScores
);

    // Detect partials (specific IDs) vs full refresh
    const isPartial = Array.isArray(params.registeredContestantIds) && params.registeredContestantIds.length > 0;

    // Defaults: partial => silent+merge; full => visible+replace
    const replaceAll = opts?.replaceAll ?? (!isPartial);
    const silent = opts?.silent ?? (isPartial);

    // Only set/loading for full visible reloads
    if (!silent && replaceAll) setRegisteredContestantsStatus('loading');

    const pid =
      (typeof params.pageantId === 'number' ? params.pageantId : undefined) ??
      pageant?.id ??
      pageantStore.CurrentPageant?.id ??
      0;

    if (!pid) {
      console.warn('fetchRegisteredContestantsDynamic: missing pageantId (skip)');
      if (!silent && replaceAll && seq === rcSeq.current) setRegisteredContestantsStatus('error');
      return [];
    }

    const sourcePageant =
      pageant?.id === pid
        ? pageant
        : pageantStore.CurrentPageant?.id === pid
          ? pageantStore.CurrentPageant
          : (await pageantStore.getPageant(pid), pageantStore.CurrentPageant);

    // if (!silent && replaceAll) showLoader?.('Loading contestants…');

    const t0 = performance.now();
    try {
      const summaries = await tabulationStore.getDynamicComprehensiveSummaries({
        pageantId: pid,
        registeredContestantIds: params.registeredContestantIds, // undefined ⇒ whole pageant
      });

      const safeSummaries = Array.isArray(summaries) ? summaries : [];
      // const mapped = safeSummaries.map((s: any) =>
      //   mapSummaryToRegisteredContestant(s, sourcePageant, allFetchedScores)
      // );

const mapped = safeSummaries.map((s: any) =>
  mapSummaryToRegisteredContestant(s, sourcePageant, scoresByContestant)
);

      // de-dupe by id (server might echo duplicates)
      const seen = new Set<number>();
      const uniqueList = mapped.filter((rc) => {
        const id = Number(rc.id);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      if (seq !== rcSeq.current) {
        // stale response — return but don’t mutate state
        return uniqueList;
      }

      // For partial refreshes that return 0 items: do not nuke/flip state
      if (uniqueList.length === 0) {
        if (!silent && replaceAll) setRegisteredContestantsStatus('success');
        return [];
      }

      if (replaceAll) {
        // Full replacement (only for full visible reloads by default)
        setRegisteredContestantsNew(uniqueList);
      } else {
        // Merge patch — preserve order & references for non-updated rows
        const patchById = new Map(uniqueList.map((rc) => [Number(rc.id), rc]));
        setRegisteredContestantsNew((prev) => {
          const seenIds = new Set<number>();
          // Replace in-place where possible
          const merged = prev.map((oldRc) => {
            const id = Number(oldRc.id);
            const newer = patchById.get(id);
            if (newer) {
              seenIds.add(id);
              return newer; // swap just this entry
            }
            return oldRc; // keep reference stable
          });
          // Append any *new* ids the previous list didn’t have (rare)
          for (const [id, rc] of patchById.entries()) {
            if (!prev.some((p) => Number(p.id) === id)) merged.push(rc);
          }
          return merged;
        });
      }

      if (!silent && replaceAll) setRegisteredContestantsStatus('success');
      return uniqueList;
    } catch (err) {
      console.error('fetchRegisteredContestantsDynamic failed:', err);
      if (!silent && replaceAll && seq === rcSeq.current) setRegisteredContestantsStatus('error');
      return [];
    } finally {
      if (!silent && replaceAll) hideLoader?.();
      // timing log optional
      // console.debug('fetchRegisteredContestantsDynamic ⏱', (performance.now() - t0).toFixed(0), 'ms');
    }
  };


  // dev-only: watch for unexpected clears/overwrites
  useEffect(() => {
    const stack = new Error("state change trace").stack;
    console.log(
      "📦 registeredContestantsNew changed:",
      { length: registeredContestantsNew?.length ?? 0, sample: registeredContestantsNew?.slice?.(0, 2) },
      "\n↳ trace:\n",
      stack
    );
  }, [registeredContestantsNew]);




  //#endregion ==========================================FETCH TABULATION SUMMARIES DYNAMICALLY END - testing==================================


  //#region===============================AREA OF COMPETITION TYPES & AWARD TYPES START =================================================


  const fetchAOCTypes = useCallback(async (): Promise<AreaOfCompetitionTypeBaseModel[]> => {
    // Some stores return the list, some just update the store; handle both.
    const res = await aocStore.getAOCTypes?.();
    const list = (Array.isArray(res) ? res : aocStore.AOCTypeList) ?? [];
    setAocTypes(list);
    return list;
  }, [aocStore]);

  const fetchAwardTypes = useCallback(async (): Promise<AwardTypeBaseModel[]> => {
    const res = await awardStore.getAwardTypes?.();
    const list = (Array.isArray(res) ? res : awardStore.AwardTypeList) ?? [];
    setAwardTypes(list);
    return list;
  }, [awardStore]);




  //#endregion===============================AREA OF COMPETITION TYPES & AWARD TYPES END =================================================


    // In the provider (keep the return type Promise<void> to match your context)
  const refetchRegisteredContestants = React.useCallback(
    async (
      filters: { pageantId?: number; registeredContestantIds?: number[] } = {},
      opts: { replaceAll?: boolean; silent?: boolean } = {}
    ): Promise<void> => {
      const pid =
        filters.pageantId ??
        pageant?.id ??
        pageantStore?.CurrentPageant?.id ??
        0;

      if (!pid) {
        console.warn("refetchRegisteredContestants: no pageantId available");
        return;
      }

      const isPartial =
        Array.isArray(filters.registeredContestantIds) &&
        filters.registeredContestantIds.length > 0;

      // Defaults: partial => merge + silent; full => replace + visible
      const replaceAll = opts.replaceAll ?? (!isPartial);
      const silent = opts.silent ?? (isPartial);

      console.log("🔄 Refetching Registered Contestants (dynamic)…", {
        pageantId: pid,
        registeredContestantIds: filters.registeredContestantIds ?? "ALL",
        replaceAll,
        silent,
      });

      await fetchRegisteredContestantsDynamic(
        {
          pageantId: pid,
          registeredContestantIds: filters.registeredContestantIds, // undefined ⇒ whole pageant
        },
        { replaceAll, silent }
      );

      console.log("✅ Refetched Registered Contestants.");
    },
    [fetchRegisteredContestantsDynamic, pageant?.id, pageantStore?.CurrentPageant?.id]
  );
  

  //#region=============================FETCHING THE  MAIN DATA START================================================================================================================
  //Dynamic Award calculating scores - awards using supreme and then others
  const awardUsesSupremeScoring = (award: any): boolean => {
    return (
      award?.specificAwardFullCriteria?.additionalScoringDetails?.isUseSupremeScoreSettings === true ||
      award?.specificAwardFullCriteria?.additionalScoringDetails?.useSupremeScoringCriteria === true ||
      award?.useSupremeScoringCriteria === true ||
      award?.useSupremeScoreSettings === true
    );
  };
  /**
 * Runs: (1) apply Supreme scores fast, then (2) per-award calculation for non-Supreme titles.
 * Scope can be pageant-wide, title-specific, or contestant-specific.
 *
 * @param filters { pageantId?, pageantAvailableTitleId?, registeredContestantId? }
 * @param opts { awardIdsOverride?: number[]; refreshEachStep?: boolean }
 */
  // helper — decide which awards we will iterate in Phase 2
  const getNonSupremeAwardIds = React.useCallback((): number[] => {
    const list = (pageantAvailableAwards ?? [])
      // exclude awards that inherit Supreme scoring
      .filter(a => !awardUsesSupremeScoring(a))
      // (optional) only keep awards that actually have scoring criteria to compute
      .filter(a => {
        const sc = a?.specificAwardFullCriteria?.scoringCriteria;
        if (sc && (sc.customRules?.length || sc.dropHighestScore || sc.dropLowestScore)) return true;
        try {
          if (a?.awardScoringCriteria) {
            const raw = JSON.parse(a.awardScoringCriteria);
            return !!(raw?.customRules?.length || raw?.dropHighestScore || raw?.dropLowestScore);
          }
        } catch { /* ignore parse errors */ }
        return false;
      });

    return list.map(a => a.id);
  }, [pageantAvailableAwards]);

const runAwardRecalcSequenceOrg = React.useCallback(
  async (
    filters: {
      pageantId?: number;
      pageantAvailableTitleId?: number;
      registeredContestantId?: number;
      registeredContestantIds?: number[];
    },
    opts?: {
      awardIdsOverride?: number[];
      refreshEachStep?: boolean;
      onAfterSupreme?: () => Promise<void>;
      onAfterAward?: (awardId: number) => Promise<void>;
    }
  ) => {
    const t0 = performance.now();
    const ts = () => `${(performance.now() - t0).toFixed(1)}ms`;

    const contestantIdsForCalculation =
      filters.registeredContestantIds?.length
        ? filters.registeredContestantIds
        : filters.registeredContestantId
          ? [filters.registeredContestantId]
          : undefined;

    console.groupCollapsed(
      '%c[Awards] runAwardRecalcSequence',
      'color:#0b5ed7;font-weight:bold',
      { filters, opts, contestantIdsForCalculation }
    );

    cancelAwardCalcRef.current = false;

    console.table(
      (pageantAvailableAwards ?? []).map((a) => ({
        id: a.id,
        name: a.name,
        rootUseSupremeScoringCriteria:
          a?.specificAwardFullCriteria?.additionalScoringDetails?.isUseSupremeScoreSettings,
        additionalIsUseSupreme:
          a?.specificAwardFullCriteria?.additionalScoringDetails?.isUseSupremeScoreSettings,
        additionalUseSupreme:
          a?.specificAwardFullCriteria?.additionalScoringDetails?.isUseSupremeScoreSettings,
        resolvedUsesSupreme: awardUsesSupremeScoring(a),
      }))
    );

    setAwardCalcProgress({
      running: true,
      phase: 'supreme',
      currentIndex: 0,
      totalAwards: 0,
      completed: 0,
      resultsByAwardId: {},
    });

    console.debug('[Awards]', ts(), 'Phase 1: Apply Supreme', {
      pageantId: filters.pageantId,
      pageantAvailableTitleId: filters.pageantAvailableTitleId,
      registeredContestantId: filters.registeredContestantId,
      registeredContestantIds: contestantIdsForCalculation,
    });

    try {
      const supremeResult =
        contestantIdsForCalculation?.length
          ? await tabulationStore.PinPointCalculateAndSaveAwardScoresFast({
              pageantId: filters.pageantId,
              pageantAvailableTitleId: opts?.awardIdsOverride,
              registeredContestantId: contestantIdsForCalculation,
            })
          : await tabulationStore.calculateAndSaveAwardScoresThatAreUsingSupremeScoreDynamic({
              pageantId: filters.pageantId,
              pageantAvailableTitleId: filters.pageantAvailableTitleId,
              registeredContestantId: filters.registeredContestantId,
            });

      console.log('[Awards] Supreme scores saved result:', supremeResult);
      await refetchRegisteredContestants();
      await opts?.onAfterSupreme?.();
    } catch (err: any) {
      console.error('[Awards]', ts(), 'Phase 1 ERROR applying Supreme:', err);

      setAwardCalcProgress((p) => ({
        ...p,
        running: false,
        phase: 'error',
        error: `Failed applying Supreme scores: ${err?.message ?? 'Unknown error'}`,
      }));

      console.groupEnd();
      return;
    }

    if (cancelAwardCalcRef.current) {
      console.warn('[Awards]', ts(), 'Cancelled after Supreme phase.');
      setAwardCalcProgress((p) => ({ ...p, running: false, phase: 'idle' }));
      console.groupEnd();
      return;
    }

    let awardIds: number[];

    if (opts?.awardIdsOverride?.length) {
      const nonSupremeSet = new Set(getNonSupremeAwardIds());
      awardIds = opts.awardIdsOverride.filter((id) => nonSupremeSet.has(id));

      console.debug('[Awards]', ts(), 'Using awardIdsOverride filtered to non-supreme:', {
        input: opts.awardIdsOverride,
        awardIds,
      });
    } else if (filters.pageantAvailableTitleId) {
      const isSupreme = !!(pageantAvailableAwards ?? []).find(
        (a) => a.id === filters.pageantAvailableTitleId && awardUsesSupremeScoring(a)
      );

      awardIds = isSupreme ? [] : [filters.pageantAvailableTitleId];

      console.debug('[Awards]', ts(), 'Single-title mode:', {
        requested: filters.pageantAvailableTitleId,
        isSupreme,
        awardIds,
      });
    } else {
      awardIds = getNonSupremeAwardIds();

      console.debug('[Awards]', ts(), 'Global mode: non-supreme awardIds:', awardIds);
    }

    const total = awardIds.length;

    setAwardCalcProgress((p) => ({
      ...p,
      phase: 'awards',
      totalAwards: total,
      completed: 0,
    }));

    if (total === 0) {
      console.info('[Awards]', ts(), 'No awards to process in Phase 2.');
    }

    for (let i = 0; i < awardIds.length; i++) {
      if (cancelAwardCalcRef.current) {
        console.warn('[Awards]', ts(), 'Cancellation requested; breaking awards loop at index', i);
        break;
      }

      const awardId = awardIds[i];
      const award = (pageantAvailableAwards ?? []).find((a) => a.id === awardId);
      const awardName = award?.name ?? `Award #${awardId}`;
      const usesSupreme = awardUsesSupremeScoring(award);

      if (usesSupreme) {
        console.warn('[Awards]', ts(), 'Skipping award unexpectedly flagged as Supreme:', {
          awardId,
          awardName,
        });
        continue;
      }

      console.groupCollapsed(
        `%c[Awards] Phase 2 → ${i + 1}/${total} ${awardName} (#${awardId})`,
        'color:#198754;font-weight:bold'
      );

      setAwardCalcProgress((p) => ({
        ...p,
        currentAwardId: awardId,
        currentAwardName: awardName,
        currentIndex: i,
      }));

      try {
        console.debug('[Awards]', ts(), 'PinPointCalculateAndSaveAwardScoresFast →', {
          pageantId: filters.pageantId,
          pageantAvailableTitleId: [awardId],
          registeredContestantId: contestantIdsForCalculation ?? '(all in pageant)',
        });

        const res: any[] | null = await tabulationStore.PinPointCalculateAndSaveAwardScoresFast({
          pageantAvailableTitleId: [awardId],
          pageantId: filters.pageantId,
          registeredContestantId: contestantIdsForCalculation,
        });

        const count = Array.isArray(res) ? res.length : 0;

        console.debug('[Awards]', ts(), 'Result returned:', {
          count,
          sample: Array.isArray(res) ? res.slice(0, 5) : res,
        });

        setAwardCalcProgress((p) => ({
          ...p,
          resultsByAwardId: { ...p.resultsByAwardId, [awardId]: res ?? null },
          completed: p.completed + 1,
        }));

        await opts?.onAfterAward?.(awardId);

        if (opts?.refreshEachStep) {
          console.debug('[Awards]', ts(), 'Refreshing contestants after this award…');
          await refetchRegisteredContestants();
          console.debug('[Awards]', ts(), 'Refresh complete.');
        }
      } catch (err: any) {
        console.error('[Awards]', ts(), `ERROR while processing ${awardName} (#${awardId}):`, err);

        setAwardCalcProgress((p) => ({
          ...p,
          resultsByAwardId: { ...p.resultsByAwardId, [awardId]: null },
          error: `Failed on ${awardName}: ${err?.message ?? 'Unknown error'}`,
          completed: p.completed + 1,
        }));
      } finally {
        console.groupEnd();
      }
    }

    if (!cancelAwardCalcRef.current) {
      console.debug('[Awards]', ts(), 'Final refetchRegisteredContestants() after loop…');
      await refetchRegisteredContestants();
      console.debug('[Awards]', ts(), 'Final refresh complete.');

      setAwardCalcProgress((p) => ({
        ...p,
        running: false,
        phase: 'done',
        currentAwardId: undefined,
        currentAwardName: undefined,
      }));

      console.info('[Awards]', ts(), 'Sequence complete.');
    } else {
      setAwardCalcProgress((p) => ({
        ...p,
        running: false,
        phase: 'idle',
        currentAwardId: undefined,
        currentAwardName: undefined,
      }));

      console.warn('[Awards]', ts(), 'Sequence ended due to cancellation.');
    }

    console.groupEnd();
  },
  [
    getNonSupremeAwardIds,
    pageantAvailableAwards,
    tabulationStore,
    refetchRegisteredContestants,
  ]
);

const runAwardRecalcSequence = React.useCallback(
  async (
    filters: {
      pageantId?: number;
      pageantAvailableTitleId?: number;
      registeredContestantId?: number;
      registeredContestantIds?: number[];
    },
    opts?: {
      awardIdsOverride?: number[];
      refreshEachStep?: boolean;
      onAfterSupreme?: () => Promise<void>;
      onAfterAward?: (awardId: number) => Promise<void>;
    }
  ) => {
    const t0 = performance.now();
    const ts = () => `${(performance.now() - t0).toFixed(1)}ms`;

    const contestantIdsForCalculation =
      filters.registeredContestantIds?.length
        ? filters.registeredContestantIds
        : filters.registeredContestantId
          ? [filters.registeredContestantId]
          : undefined;

    const getSupremeAwardIds = (): number[] =>
      (pageantAvailableAwards ?? [])
        .filter((award) => awardUsesSupremeScoring(award))
        .map((award) => Number(award.id))
        .filter((id) => Number.isFinite(id) && id > 0);

    const supremeAwardIds = opts?.awardIdsOverride?.length
      ? opts.awardIdsOverride
          .map(Number)
          .filter((id) => getSupremeAwardIds().includes(id))
      : getSupremeAwardIds();

    console.groupCollapsed(
      '%c[Awards] runAwardRecalcSequence',
      'color:#0b5ed7;font-weight:bold',
      { filters, opts, contestantIdsForCalculation, supremeAwardIds }
    );

    cancelAwardCalcRef.current = false;

    setAwardCalcProgress({
      running: true,
      phase: 'supreme',
      currentIndex: 0,
      totalAwards: supremeAwardIds.length,
      completed: 0,
      resultsByAwardId: {},
    });

    try {
      if (supremeAwardIds.length > 0) {
        const supremeResult = await tabulationStore.PinPointCalculateAndSaveAwardScoresFast({
          pageantId: filters.pageantId,
          pageantAvailableTitleId: supremeAwardIds,
          registeredContestantId: contestantIdsForCalculation,
        });

        console.log('[Awards] Supreme awards saved result:', supremeResult);
      } else {
        console.info('[Awards]', ts(), 'No supreme-scoring awards to process in Phase 1.');
      }

      await opts?.onAfterSupreme?.();
    } catch (err: any) {
      console.error('[Awards]', ts(), 'Phase 1 ERROR applying Supreme:', err);

      setAwardCalcProgress((p) => ({
        ...p,
        running: false,
        phase: 'error',
        error: `Failed applying Supreme scores: ${err?.message ?? 'Unknown error'}`,
      }));

      console.groupEnd();
      return;
    }

    if (cancelAwardCalcRef.current) {
      console.warn('[Awards]', ts(), 'Cancelled after Supreme phase.');

      setAwardCalcProgress((p) => ({
        ...p,
        running: false,
        phase: 'idle',
      }));

      console.groupEnd();
      return;
    }

    let awardIds: number[];

    if (opts?.awardIdsOverride?.length) {
      const nonSupremeSet = new Set(getNonSupremeAwardIds());

      awardIds = opts.awardIdsOverride
        .map(Number)
        .filter((id) => nonSupremeSet.has(id));

      console.debug('[Awards]', ts(), 'Phase 2 awardIdsOverride filtered to non-supreme:', {
        input: opts.awardIdsOverride,
        awardIds,
      });
    } else if (filters.pageantAvailableTitleId) {
      const requestedAward = (pageantAvailableAwards ?? []).find(
        (award) => Number(award.id) === Number(filters.pageantAvailableTitleId)
      );

      const isSupreme = awardUsesSupremeScoring(requestedAward);

      awardIds = isSupreme ? [] : [Number(filters.pageantAvailableTitleId)];

      console.debug('[Awards]', ts(), 'Single-title mode:', {
        requested: filters.pageantAvailableTitleId,
        isSupreme,
        awardIds,
      });
    } else {
      awardIds = getNonSupremeAwardIds();

      console.debug('[Awards]', ts(), 'Global mode: non-supreme awardIds:', awardIds);
    }

    setAwardCalcProgress((p) => ({
      ...p,
      phase: 'awards',
      totalAwards: awardIds.length,
      completed: 0,
      currentAwardId: undefined,
      currentAwardName: undefined,
    }));

    for (let i = 0; i < awardIds.length; i++) {
      if (cancelAwardCalcRef.current) {
        console.warn('[Awards]', ts(), 'Cancellation requested; breaking awards loop at index', i);
        break;
      }

      const awardId = awardIds[i];
      const award = (pageantAvailableAwards ?? []).find(
        (candidate) => Number(candidate.id) === Number(awardId)
      );
      const awardName = award?.name ?? `Award #${awardId}`;

      if (awardUsesSupremeScoring(award)) {
        console.warn('[Awards]', ts(), 'Skipping award unexpectedly flagged as Supreme:', {
          awardId,
          awardName,
        });
        continue;
      }

      setAwardCalcProgress((p) => ({
        ...p,
        currentAwardId: awardId,
        currentAwardName: awardName,
        currentIndex: i,
      }));

      try {
        const result = await tabulationStore.PinPointCalculateAndSaveAwardScoresFast({
          pageantAvailableTitleId: [awardId],
          pageantId: filters.pageantId,
          registeredContestantId: contestantIdsForCalculation,
        });

        setAwardCalcProgress((p) => ({
          ...p,
          resultsByAwardId: {
            ...p.resultsByAwardId,
            [awardId]: result ?? null,
          },
          completed: p.completed + 1,
        }));

        await opts?.onAfterAward?.(awardId);

        if (opts?.refreshEachStep) {
          await refetchRegisteredContestants();
        }
      } catch (err: any) {
        console.error('[Awards]', ts(), `ERROR while processing ${awardName} (#${awardId}):`, err);

        setAwardCalcProgress((p) => ({
          ...p,
          resultsByAwardId: {
            ...p.resultsByAwardId,
            [awardId]: null,
          },
          error: `Failed on ${awardName}: ${err?.message ?? 'Unknown error'}`,
          completed: p.completed + 1,
        }));
      }
    }

    if (!cancelAwardCalcRef.current) {
      await refetchRegisteredContestants();

      setAwardCalcProgress((p) => ({
        ...p,
        running: false,
        phase: 'done',
        currentAwardId: undefined,
        currentAwardName: undefined,
      }));
    } else {
      setAwardCalcProgress((p) => ({
        ...p,
        running: false,
        phase: 'idle',
        currentAwardId: undefined,
        currentAwardName: undefined,
      }));
    }

    console.groupEnd();
  },
  [
    getNonSupremeAwardIds,
    pageantAvailableAwards,
    tabulationStore,
    refetchRegisteredContestants,
  ]
);

  const cancelAwardRecalcSequence = React.useCallback(() => {
    cancelAwardCalcRef.current = true;
  }, []);

  const fetchContestantCriteriaAndScoringCriteria = async (
    titleId: number
  ): Promise<PageantAvailableTitleModel | null> => {
    try {
      const updatedTitle = await awardStore.getAward(titleId);

      if (!updatedTitle) {
        console.warn("No updated data received for the title.");
        return null;
      }

      return updatedTitle;
    } catch (error) {
      console.error("Error fetching award criteria:", error);
      return null;
    }
  };


  //Fetching all the available titles and awards in a pageant
  const fetchPageantAvailableAwardsDynamic = async (filters: {
    pageantId?: number;
    titleId?: number;
    awardTypeId?: number;
    registeredPageantContestantId?: number;
    pageantAvailablePackageId?: number;
  }): Promise<PageantAvailableAward[]> => {
    try {
      const data = await tabulationStore.getAvailableAwardsDynamic(filters);

      const mappedAwards: PageantAvailableAward[] = (data ?? [])
        .map((award: Partial<PageantAvailableAward> & { [k: string]: any }): PageantAvailableAward => {


          const specificAwardFullCriteria: SpecificAwardFullCriteria = {
            scoringCriteria: parseScoringCriteriaNew(award.awardScoringCriteria),
            additionalScoringDetails: parseAdditionalScoringDetails(award),
            contestantCriteria: parseContestantCriteriaNew(award.contestantEligibleWinnerCriteria),
          };

          // use relationships from server (camelCase if System.Text.Json)
          const rawRelsAny: any[] = Array.isArray(award.matchingAwardRelationships)
            ? award.matchingAwardRelationships
            : [];

          // Handle both new DTO and legacy array-of-base-models
          const matchingAwardRelationships: MatchingAwardRelationships[] = rawRelsAny.map((r: any) => {
            // New DTO (camelCase or PascalCase)
            const rel =
              r?.awardRelationship ??
              r?.registeredContestantAwardRelationship ?? // typical System.Text.Json camelCase
              r?.RegisteredContestantAwardRelationship;   // PascalCase fallback

            const criteria =
              r?.criteriaUIString ??
              r?.CriteriaUIString ??
              '';

            if (rel) {
              return {
                awardRelationship: rel as RegisteredContestantAwardRelationshipBaseModel,
                criteriaUIString: String(criteria),
              };
            }

            // Legacy: the item itself is the base model
            return {
              awardRelationship: r as RegisteredContestantAwardRelationshipBaseModel,
              criteriaUIString: '',
            };
          }).sort(
            (a, b) =>
              (a.awardRelationship.awardIndex ?? Number.MAX_SAFE_INTEGER) -
              (b.awardRelationship.awardIndex ?? Number.MAX_SAFE_INTEGER)
          );


          return {
            id: Number(award.id) ?? 0,
            name: award.name ?? '',
            pageantId: Number(award.pageantId) ?? 0,
            awardTypeId: Number(award.awardTypeId) ?? 0,
            awardType: award.awardType,

            price: Number(award.price) ?? 0,
            cashPrize: Number(award.cashPrize) ?? 0,
            isSplitThePot: award.isSplitThePot ?? false,
            numberAvailable: Number(award.numberAvailable) ?? 0,

            awardScoringCriteria: award.awardScoringCriteria ?? '',
            contestantEligibleWinnerCriteria: award.contestantEligibleWinnerCriteria ?? '',

            specificAwardFullCriteria,

            pullOrder: Number.isFinite(Number(award.pullOrder)) ? Number(award.pullOrder) : Number.MAX_SAFE_INTEGER,
            isReigningRoyalty: !!award.isReigningRoyalty,
            note: award.note ?? '',

            includedInPackages: award.includedInPackages ?? [],
            enteredContestants: award.enteredContestants ?? [],

            matchingAwardRelationships,
          };
        })
        .sort((a, b) => (a.pullOrder ?? 0) - (b.pullOrder ?? 0));

      // Enrich with backend maxPossiblePoints (parallel)
      const awardsWithMax = await Promise.all(
        mappedAwards.map(async (aw) => {
          try {
            const { maxPossiblePoints } = await tabulationStore.getMaxPossiblePointsForAward(aw.pageantId, aw.id);
            return {
              ...aw,
              specificAwardFullCriteria: {
                ...aw.specificAwardFullCriteria,
                scoringCriteria: {
                  ...aw.specificAwardFullCriteria?.scoringCriteria,
                  maxPossiblePoints: maxPossiblePoints ?? 0,
                },
              },
            } as PageantAvailableAward;
          } catch (e) {
            console.warn('Max points fetch failed for award', aw.id, e);
            return aw;
          }
        })
      );

      // Merge into state (replace by id)
      setPageantAvailableAwards((prev) => {
        const idsToReplace = new Set(awardsWithMax.map((a) => a.id));
        const others = prev.filter((a) => !idsToReplace.has(a.id));
        return [...others, ...awardsWithMax];
      });

      return awardsWithMax;
    } catch (error) {
      console.error('Error dynamically fetching available awards:', error);
      toast.error('Failed to fetch pageant awards.');
      return [];
    }
  };




  // assumes you already have `awardRelationships: RegisteredContestantAwardRelationshipBaseModel[]`
  // in scope from your provider (state/context).





  const refetchPageantAvailableAwards = useCallback(
    async (filters: {
      pageantId?: number;
      titleId?: number;
      awardTypeId?: number;
      registeredPageantContestantId?: number;
      pageantAvailablePackageId?: number;
    } = {}) => {
      const finalFilters = {
        pageantId: filters.pageantId ?? pageant?.id,
        titleId: filters.titleId,
        awardTypeId: filters.awardTypeId,
        registeredPageantContestantId: filters.registeredPageantContestantId,
        pageantAvailablePackageId: filters.pageantAvailablePackageId,
      };

      await fetchPageantAvailableAwardsDynamic(finalFilters);
    },
    [fetchPageantAvailableAwardsDynamic, pageant?.id]
  );

  //Fetching all the available aocs in a pageant


  const fetchPageantAvailableAOCDynamic = async (filters: {
    pageantId?: number;
    aocId?: number;
    aocTypeId?: number;
    registeredPageantContestantId?: number;
    pageantAvailablePackageId?: number;
  }): Promise<PageantAvailableAOC[]> => {
    try {
      const data = await tabulationStore.getAvailableAOCDynamic(filters);

      const toOrder = (n: number | null | undefined) =>
        typeof n === 'number' && !Number.isNaN(n) ? n : Number.MAX_SAFE_INTEGER;

      const mappedAOC: PageantAvailableAOC[] = (data ?? [])
        .map((aoc: any) => {
          // Base lists from server
          const enteredRaw = (aoc?.competingInContestants ?? []).map((r: any) => ({
            registeredPageantContestantId: Number(r?.registeredPageantContestantId ?? r?.contestantId ?? r?.id ?? 0),
            contestantName: String(r?.contestantName ?? ''),
            entrySource: String(r?.entrySource ?? ''),
            isEntered: true,
          }));

          const notEntered = (aoc?.notEnteredContestants ?? []).map((r: any) => ({
            registeredPageantContestantId: Number(r?.registeredPageantContestantId ?? r?.contestantId ?? r?.id ?? 0),
            contestantName: String(r?.contestantName ?? ''),
          }));

          // Expand “All Contestants” sentinel (id === 0) to every registered contestant (optional but handy for counts)
          let availableToCompeteContestants = enteredRaw;
          const hasAllSentinel = enteredRaw.some(e => e.registeredPageantContestantId === 0);
          if (hasAllSentinel) {
            const src = enteredRaw.find(e => e.registeredPageantContestantId === 0)?.entrySource
              ?? 'Free AOC - automatically available to everyone';

            availableToCompeteContestants = (registeredContestantsNew ?? []).map((c: any) => ({
              registeredPageantContestantId: Number(c?.id ?? 0),
              contestantName: c?.fullName ?? `${c?.firstName ?? ''} ${c?.lastName ?? ''}`.trim(),
              entrySource: src,
              isEntered: true,
            }));
          }

          return {
            id: aoc?.id ?? 0,
            name: aoc?.name ?? '',
            pageantId: aoc?.pageantId ?? 0,
            aocTypeId: aoc?.aocTypeId ?? 0,
            competeOrder: aoc?.competeOrder,
            price: aoc?.price ?? 0,
            isOptional: aoc?.isOptional ?? null,
            note: aoc?.note ?? '',
            aocScoreCategories: aoc?.aocScoringCategories ?? [],
            includedInPackages: aoc?.includedInPackages ?? [],
            // normalized/expanded lists:
            availableToCompeteContestants,
            notEnteredContestants: notEntered,
          } as PageantAvailableAOC;
        })
        .sort((a, b) => toOrder(a.competeOrder) - toOrder(b.competeOrder));

      // Points per AOC
      const pointsMap: Record<number, number> = {};

      mappedAOC.forEach(aoc => {
        pointsMap[aoc.id] = getAocMaxPoints(aoc.id, supremeScoringSettings, false);
      });



      setTotalPointsPossible(pointsMap);



      // Replace existing by id
      setPageantAvailableAOC(prev => {
        const ids = new Set(mappedAOC.map(a => a.id));
        const others = prev.filter(a => !ids.has(a.id));
        return [...others, ...mappedAOC];
      });

      return mappedAOC;
    } catch (error) {
      console.error('Error dynamically fetching available AOCs:', error);
      toast.error('Failed to fetch pageant AOCs.');
      return [];
    }
  };



  const refetchPageantAvailableAOC = useCallback(
    async (filters: {
      pageantId?: number;
      aocId?: number;
      aocTypeId?: number;
      registeredPageantContestantId?: number;
      pageantAvailablePackageId?: number;
    } = {}) => {
      const finalFilters = {
        pageantId: filters.pageantId ?? pageant?.id,
        aocId: filters.aocId,
        aocTypeId: filters.aocTypeId,
        registeredPageantContestantId: filters.registeredPageantContestantId,
        pageantAvailablePackageId: filters.pageantAvailablePackageId,
      };

      await fetchPageantAvailableAOCDynamic(finalFilters);
    },
    [fetchPageantAvailableAOCDynamic, pageant?.id]
  );


  //Fetch pageant supreme score criteria
  const handleSupremeScoreSetupNew = (supremeSettings: ScoringCriteria) => {
    setSupremeScoringSettings(supremeSettings);
  };

  // 1. Fetch & set Supreme Criteria
  const fetchAndSetupSupremeCriteria = async (pageantId: number) => {
    try {
      const apiData = await tabulationStore.fetchSupremeScoringCriteria(pageantId);
      if (!apiData) return;

      const mapped = {
        dropHighestScore: apiData.dropHighestScore ?? false,
        dropLowestScore: apiData.dropLowestScore ?? false,
        customRules: (apiData.customRules ?? []).map(rule => ({
          topN: rule.topN ?? 0,
          aocIds: rule.aocIds ?? [],
          aocNames: (rule.aocIds ?? [])
            .map(aocId => pageantAvailableAOC.find(x => x.id === aocId)?.name)
            .filter((name): name is string => Boolean(name)), // Ensures string[] only
          selectedAocTypeId: rule.selectedAocTypeId ?? undefined,
          maxCountForSelectedAocType: rule.maxCountForSelectedAocType ?? undefined,
          specificScoringCategoryIds: rule.specificScoringCategoryIds ?? [],
        })),
      };

      setSupremeScoringSettings(mapped);



    } catch (error) {
      console.error("❌ Failed to load Supreme Scoring Criteria:", error);
    }
  };

  const refetchSupremeCriteriaAndSupremeScores = useCallback(async () => {
    if (!pageant?.id) return;

    await fetchAndSetupSupremeCriteria(pageant.id);
    // await fetchRegisteredContestants();
    await tabulationStore.calculateAndSaveAllSupremeScoresInPageant(pageant.id);
  }, [pageant?.id, fetchAndSetupSupremeCriteria]);

  //Reset award scores dynamically - either by whole pageant or specific awards
  const resetAwardRelationshipScoresDynamic = async (filters: {
    pageantAvailableTitleIds?: number[];
    registeredContestantIds?: number[];
    pageantId?: number;
  }): Promise<any[] | null> => {
    try {
      showLoader("Resetting award scores...");

      const result = await tabulationStore.resetAwardRelationshipsScoresDynamic(filters);

      // Refresh local data so UI reflects reset
      await Promise.all([
        refetchRegisteredContestants(),
        refetchAwardRelationships({
          pageantId: filters.pageantId ?? pageant?.id,
        }),
        refetchPageantAvailableAwards({
          pageantId: filters.pageantId ?? pageant?.id,
        }),
      ]);

      return result;
    } catch (e) {
      console.error("resetAwardRelationshipsDynamic failed:", e);
      return null;
    } finally {
      hideLoader();
    }
  };



  //Reset award relationships scores dynamically - either by pageantId or an array of title or contestantIds
  const resetAwardScoringCriteriaDynamic = async (filters: {
    pageantAvailableTitleIds?: number[];
    pageantId?: number;
  }): Promise<any[] | null> => {
    try {
      showLoader("Resetting award scoring criteria…");

      const result = await tabulationStore.resetAwardScoringCriteriaDynamic(filters);

      // Refresh local data so UI reflects reset
      await Promise.all([
        refetchRegisteredContestants(),
        refetchAwardRelationships({
          pageantId: filters.pageantId ?? pageant?.id,
        }),
        refetchPageantAvailableAwards({
          pageantId: filters.pageantId ?? pageant?.id,
        }),
      ]);

      return result;
    } catch (e) {
      console.error("resetAwardScoringCriteriaDynamic failed:", e);
      return null;
    } finally {
      hideLoader();
    }
  };


  // Fetching All award relationships (all the titles and awards available to each registered contestants: isEntered, MeetsCriteria, IsWon, Score Towards Award, All the Criteria for that award)
  const fetchAwardRelationshipsDynamic = async (filters: {
    registeredContestantId?: number;
    pageantAvailableTitleId?: number;
    pageantId?: number;
  }) => {
    try {
      const data = await tabulationStore.getAwardRelationshipsDynamic(filters);
      const mappedAwardRelationships = (data ?? []).map((relationship) => ({
        id: relationship.id ?? 0,
        registeredPageantContestantId: relationship.registeredPageantContestantId ?? 0,
        pageantAvailableTitleId: relationship.pageantAvailableTitleId ?? 0,
        isEntered: relationship.isEntered ?? false,
        isWon: relationship.isWon ?? false,
        calculationNeeded: relationship.calculationNeeded ?? true,
        doubleCrown: relationship.doubleCrown ?? false,
        useSupremeScoringCriteria: relationship.useSupremeScoringCriteria ?? false,
        awardScoreBasedOnScoringCriteria: relationship.awardScoreBasedOnScoringCriteria ?? 0,
        meetsContestantCriteria: relationship.meetsContestantCriteria ?? false,
        awardTypeId: relationship.awardTypeId ?? 0,
        awardScoringCriteriaDescription: relationship.awardScoringCriteriaDescription ?? "",
        awardIndexContestantCriteriaDescription: relationship.awardIndexContestantCriteriaDescription ?? "",
        awardIndex: relationship.awardIndex ?? 0,
      }));

      // Optional: you could either replace all, or just patch relevant ones
      setAwardRelationships(prev => {
        // Replace existing relationships that match and keep the rest
        const idsToReplace = new Set(mappedAwardRelationships.map(rel => rel.id));
        const others = prev.filter(rel => !idsToReplace.has(rel.id));
        return [...others, ...mappedAwardRelationships];
      });

      return mappedAwardRelationships;
    } catch (error) {
      console.error("Error dynamically fetching award relationships:", error);
      toast.error("Failed to fetch specific award relationships.");
      return [];
    }
  };
  //Refetch award relationships after their are updated - only the ones that need it - this is dynamic
  const refetchAwardRelationships = useCallback(
    async (filters: {
      registeredContestantId?: number;
      pageantAvailableTitleId?: number;
      pageantId?: number;
    } = {}) => {
      // If no filters are passed, default to current pageant
      const finalFilters = {
        pageantId: filters.pageantId ?? pageant?.id,
        registeredContestantId: filters.registeredContestantId,
        pageantAvailableTitleId: filters.pageantAvailableTitleId,
      };

      await fetchAwardRelationshipsDynamic(finalFilters);
    },
    [fetchAwardRelationshipsDynamic, pageant?.id]
  );

  //Update Eligibility Flags: isEntered, MeetsCriteria, FullyEligibile
  const updateEligibilityFlagsDynamic = React.useCallback(
    async (filters: {
      registeredContestantId?: number;
      pageantAvailableTitleId?: number; // awardId
      pageantId?: number;
    } = {}) => {
      const pageantId = filters.pageantId ?? pageant?.id;
      if (!pageantId) {
        console.warn("updateEligibilityFlagsDynamic: missing pageantId");
        return;
      }

      try {
        await tabulationStore.updateEligbilityFlagsDynamic({
          pageantId,
          pageantAvailableTitleId: filters.pageantAvailableTitleId,
          registeredPageantContestantId: filters.registeredContestantId,
        });
      } catch (err) {
        console.error("updateEligibilityFlagsDynamic failed", err);
      }
    },
    [tabulationStore, pageant?.id]
  );



  //Fetching all the individual scores for each registered contestant by aoc and judge

  const fetchReceivedScoresDynamic = async (filters: {
    pageantId?: number;
    registeredPageantContestantId?: number;
    judgeId?: number;
    aocId?: number;
    aocScoringCategoryId?: number;
  }) => {
    try {
      const data = await tabulationStore.getReceivedScoresDynamically(filters);

      if (!data || !Array.isArray(data)) return [];

      const grouped: { [contestantId: number]: ReceivedScoreBaseModel[] } = {};

      for (const score of data) {
        const contestantId = score.registeredPageantContestantId;
        if (contestantId === undefined || contestantId === null) continue;

        if (!grouped[contestantId]) {
          grouped[contestantId] = [];
        }

        grouped[contestantId].push(score);
      }

setAllFetchedScores((prev) => {
  const updated = { ...prev };
  const refreshedJudgeId = filters.judgeId ? Number(filters.judgeId) : null;

  for (const [contestantIdStr, scores] of Object.entries(grouped)) {
    const contestantId = Number(contestantIdStr);
    const existingScores = prev[contestantId] ?? [];

    if (!refreshedJudgeId) {
      updated[contestantId] = scores;
      continue;
    }

    const otherJudgeScores = existingScores.filter((existingScore: any) => {
      const existingJudgeId = Number(
        existingScore?.judgeId ??
          existingScore?.JudgeId ??
          existingScore?.eventJudgeId ??
          existingScore?.EventJudgeId ??
          0
      );

      return existingJudgeId !== refreshedJudgeId;
    });

    updated[contestantId] = [...otherJudgeScores, ...scores];
  }

  return updated;
});

      return data;
    } catch (error) {
      console.error("Error dynamically fetching received scores:", error);
      toast.error("Failed to fetch specific scores.");
      return [];
    }
  };



  const refetchReceivedScores = useCallback(
    async (
      filters: {
        pageantId?: number;
        registeredPageantContestantId?: number;
        judgeId?: number;
        aocId?: number;
        aocScoringCategoryId?: number;
      } = {},
      opts?: { skipPresenceCheck?: boolean }
    ): Promise<ReceivedScoreBaseModel[]> => {
      console.log("Refetching Scores...");
      // fill pageantId if omitted
      const finalFilters = {
        pageantId: filters.pageantId ?? pageant?.id,
        registeredPageantContestantId: filters.registeredPageantContestantId,
        judgeId: filters.judgeId,
        aocId: filters.aocId,
        aocScoringCategoryId: filters.aocScoringCategoryId,
      };

      if (!opts?.skipPresenceCheck) {
        const hasAny = await tabulationStore.checkIfScoresExistDynamically(finalFilters);
        if (!hasAny) {
          const cid = finalFilters.registeredPageantContestantId;
          if (cid != null) {
            // cache empty once so we don’t re-fetch in loops
          setAllFetchedScores(prev => ({
  ...prev,
  [cid]: [],
}));
          }
          console.log("No scores to fetch", finalFilters);
          return [];
        }
      }

      const data = await fetchReceivedScoresDynamic(finalFilters);
      return Array.isArray(data) ? data : [];
    },
    [fetchReceivedScoresDynamic, tabulationStore, pageant?.id]
  );

const refetchContestantScoresForLiveUpdate = useCallback(
  async (contestantId: number) => {
    if (!contestantId) return [];

    return await refetchReceivedScores(
      { registeredPageantContestantId: contestantId },
      { skipPresenceCheck: false }
    );
  },
  [refetchReceivedScores],
);

  // Fetch pageant age groups and order them youngest to oldest



  const fetchAgeGroups = async (pageantId?: number) => {
    const pg = await ensureCurrentPageant(pageantId);
    if (!pg) { setAgeGroupsOrdered([]); return; }
    const current = (pg.pageantAgeGroups ?? []).filter(g => !g.isSupreme);
    setAgeGroupsOrdered(orderPageantAgeGroupsYoungestToOldest(current));
  };

  const fetchSupremeGroups = async (pageantId?: number) => {
    const pg = await ensureCurrentPageant(pageantId);
    if (!pg) { setSupremeGroupsOrdered([]); return; }
    const current = (pg.pageantAgeGroups ?? []).filter(g => g.isSupreme);
    setSupremeGroupsOrdered(orderPageantAgeGroupsYoungestToOldest(current));
  };

  const fetchEventDivisions = async (pageantId?: number) => {
    const pg = await ensureCurrentPageant(pageantId);
    if (!pg) { setEventDivisions([]); return; }
    const current = await pageantStore.getAllPageantDivisions(pageantId);
    setEventDivisions(current);
  };


  // Fetch Judges with additional user information


  const fetchJudges = async (eventId?: number) => {
    const ev = await ensureCurrentEvent(eventId);
    if (!ev) {
      setJudges([]);
      return;
    }

    try {
      const judges = await judgeStore.getJudgesByEventId(eventId ?? 0);

      setJudges(judges);

      console.log("New fetching judges:", judges);

      // 🔍 Debug: verify AOC assignment per judge
      console.group("🧑‍⚖️ Judges + AOC Assignments");
      judges.forEach(j => {
        console.log({
          judgeId:
            j?.id,
          aocIds: getJudgeAocIds(j),
          raw: j
        });
      });
      console.groupEnd();

      return judges;
    } catch (error) {
      console.error("Error fetching judges NEW:", error);
      return [];
    }
  };

  // const fetchPersonnel = async (pageantId?: number) => {
  //   const pg = await ensureCurrentPageant(pageantId);
  //   if (!pg) {
  //     setPersonnel([]);
  //     return;
  //   }

  //   try {
  //     const judges = await eventStore.getJudgesByPageantId(pageantId ?? 0);

  //     const personnelWithInfo = await Promise.all(
  //       judges.map(async (judge) => {
  //         const userInfo = await userStore.getUserBasicInfo(judge.personnelUserId);

  //         return {
  //           ...judge,
  //           firstName: userInfo?.firstName ?? "",
  //           lastName: userInfo?.lastName ?? "",
  //           emailAddress: userInfo?.emailAddress ?? "",
  //         };
  //       })
  //     );

  //     // ⭐ Sort by personnelUserId (ascending)
  //     personnelWithInfo.sort(
  //      (a, b) => (a.personnelUserId ?? 0) - (b.personnelUserId ?? 0)
  //     );

  //     setPersonnel(personnelWithInfo);
  //     setNumJudges(judges.length);

  //     return personnelWithInfo;
  //   } catch (error) {
  //     console.error("Error fetching judges:", error);
  //     return [];
  //   }
  // };



  //Fetching all registered contestants


  //Fetching contestants attributes (age group, supreme groups, level, compstyle, gender)
  const fetchContestantAttributes = async (pageantId: number) => {
    await tabulationStore.getContestantAttributesByPageant(pageantId);
  };


  //Fetch ALL registered contestants in an events scores in detail: broken down by aoc, judge, with and without drop score logic, grand total and final total with drop score logic
  const fetchTotalScoresWithDropsForContestantAsyncAll = async (contestantId: number) => {
    try {
      // const response = await fetch(`https://localhost:7148/ReceivedScores/GetTotalScoresWithDropsForContestantByAOCAll/${contestantId}`);

      const response = await pageantRegistrationStore.getTotalScoresWithDropsForContestantAsyncAll(contestantId);
      const data = await response.json(); // Parse the response body to JSON
      console.log('Total scores received:', data);  // Log the parsed data

      return data;  // Return the parsed data (this should contain the actual data you're expecting)
    } catch (error) {
      console.error('Error fetching total scores:', error);
      return {};  // Return an empty object in case of error
    }
  };





  //===================TABULATION STATUS start - testing=====================

  const refresh = useCallback(() => {
    const next = computeTabulationStatus({
      registeredContestants: registeredContestantsNew,
      judges: judges,                 // raw personnel
      aocs: pageantAvailableAOC,         // raw AOCs
      awards: pageantAvailableAwards,    // raw awards
      allScoresByContestant: allFetchedScores, // raw scores map
      // optional:
      // isContestantActive,
      // isContestantEnteredInAOC,
      // isJudgeActive,
      // formatContestantName,
    });
    setTabulationStatus(next);
  }, [
    registeredContestantsNew,
    judges,
    pageantAvailableAOC,
    pageantAvailableAwards,
    allFetchedScores,
  ]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  //===================TABULATION STATUS end - testing=====================




  const fetchAllScoreAndTallyData = async (pageantId: number): Promise<void> => {
    if (!pageantId) {
      console.warn('fetchAllScoreAndTallyData: missing pageantId');
      return;
    }

    showLoader('Fetching all score and tally data...');
    setIsDataLoaded(false);
    setIsLoadingScoring(true);

    try {
      // Ensure the correct pageant is loaded first
      if (pageantStore.CurrentPageant?.id !== pageantId) {
        await pageantStore.getPageant(pageantId);
        if (pageantStore.CurrentPageant?.id !== pageantId) return; // navigated away
      }

      // Lookups (AOCs, awards, divisions)
      showLoader('Begin Tabulation...');
      await Promise.allSettled([fetchAOCTypes(), fetchAwardTypes()]);

      showLoader('Syncing Award Relationships...');
      await tabulationStore.syncAwardsWithRelationshipsDynamic({ pageantId });
      await tabulationStore.updateEligbilityFlagsDynamic({ pageantId });

      // Scores presence gate
      showLoader('Checking for any scores...');

let contestantScoresById: ScoresByContestant = {};

const hasAnyForPageant =
  await tabulationStore.checkIfScoresExistDynamically({ pageantId });

if (hasAnyForPageant) {
  showLoader('Fetching all contestant scores.');

  const fetchedScores = await fetchReceivedScoresDynamic({ pageantId });

  contestantScoresById = normalizeScoresByContestant(fetchedScores);

  setAllFetchedScores(contestantScoresById);
} else {
  console.log('No Scores Yet', { pageantId });
}

      // AOCs
      showLoader('Fetching areas of competition...');
      const pageantAvailableAOCData = await fetchPageantAvailableAOCDynamic({ pageantId });
      // console.log('Pageant Available AOC Data (tabulation provider)', pageantAvailableAOCData, pageantId);

      if (!pageantAvailableAOCData || pageantAvailableAOCData.length === 0) {
        console.warn('No AOCs found. Skipping related fetches.');
        setIsDataLoaded(true);
        return;
      }

      // Divisions and Age Groups
      showLoader('Fetching divisions, age and supreme groups...');
      await fetchEventDivisions(pageantId);
      await fetchAgeGroups(pageantId);
      await fetchSupremeGroups(pageantId);

      // Judges
      showLoader('Fetching judges...');
      // await fetchPersonnel(pageantId);
      const eventId = pageantStore.CurrentPageant?.eventId;
      if (eventId) {
        await fetchJudges(eventId);
      } else {
        console.warn('No eventId found for pageant', pageantId);
      }

      // Awards
      showLoader('Fetching awards...');
      await fetchPageantAvailableAwardsDynamic({ pageantId });
      // await fetchAwardRelationshipsDynamic({ pageantId });

      // Supreme criteria
      showLoader('Fetching Supreme Score Criteria...');
      await fetchAndSetupSupremeCriteria(pageantId);

      // Contestants
      showLoader('Fetching all registered contestants...');
      // await fetchRegisteredContestantsDynamic({ pageantId }, { replaceAll: true, silent: true });
// const contestantScores = await fetchReceivedScoresDynamic({ pageantId });

await fetchRegisteredContestantsDynamic(
  { pageantId },
  {
    replaceAll: true,
  scoresByContestantOverride: contestantScoresById,
  }
);
      // await fetchContestantAttributes(pageantId);
      // await fetchRegisteredContestants(pageantId);

      setIsDataLoaded(true);
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsDataLoaded(true);
    } finally {
      setIsLoadingScoring(false);
      hideLoader();
    }
  };


  //++++++++++++=+++++++++FETCH ALL END++++++++++++++++++++++



  //#endregion=============================FETCHING THE  MAIN DATA END================================================================================================================


  //#region===========================================GENERATING CRITERIA STRING HELPERS START====================================
  //#region =====================================SUPREME SCORE GENERATED STRING START==========================================


  //#endregion =====================================SUPREME SCORE GENERATED STRING END==========================================


  //#region =====================================GENERIC AWARD SCORING GENERATED STRING START==========================================

  //#endregion =====================================GENERIC AWARD SCORING CRITERIA GENERATED STRING END==========================================


  //#region =====================================GENERIC AWARD CONTESTANT CRITERIA GENERATED STRING START==========================================

  interface GeneralContestantCriteria {
    awardIndex: number;
    criteria: {
      ageGroups: string[];
      ageGroupIds: number[];
      levels: string[];
      competeStyles: string[];
      genderIds: number[];
      genders: string[];
      supremeGroups: string[];
      supremeGroupIds: number[];
      divisionIds: number[];
      divisions: string[];
    };
  }

  const generateContestantCriteriaString = (contestantCriteriaString: string): string => {
    // Check for null or invalid values
    if (!contestantCriteriaString || contestantCriteriaString === "N/A") {
      return "No contestant criteria available.";
    }

    let contestantCriteria: GeneralContestantCriteria[];

    // Attempt to parse the JSON string
    try {
      contestantCriteria = JSON.parse(contestantCriteriaString);
    } catch (error) {
      console.error("Error parsing contestant criteria JSON:", error);
      return "Invalid scoring criteria data.";
    }

    // Initialize the base result array
    const result: string[] = [];

    // Iterate over each contestant criterion
    contestantCriteria.forEach((criteriaObj, index) => {
      const { criteria } = criteriaObj;
      let criteriaString = `Award ${index + 1}: `; // Using index + 1 for awardIndex

        const divisionIds = criteria.divisionIds ?? [];
  const divisions = criteria.divisions ?? [];

      // Check if all criteria are "ALL" or null/empty array
      const allCriteriaEmpty = (!criteria.ageGroups || criteria.ageGroups.includes("ALL") && criteria.ageGroups.length === 1) &&
        (!criteria.supremeGroups || criteria.supremeGroups.includes("ALL") && criteria.supremeGroups.length === 1) &&
        (!criteria.levels || criteria.levels.includes("ALL") && criteria.levels.length === 1) &&
        (!criteria.competeStyles || criteria.competeStyles.includes("ALL") && criteria.competeStyles.length === 1) &&
        (!criteria.genderIds || criteria.genderIds.length === 0) &&
        (!criteria.genders || criteria.genders.includes("ALL") && criteria.genders.length === 1) &&
       (!divisions.length && !divisionIds.length);

      // If all criteria are empty, set criteriaString to "Available to All"
      if (allCriteriaEmpty) {
        criteriaString += "Available to All.";
      } else {
        // Check and append age group criteria
        if (criteria.ageGroups && criteria.ageGroups.length > 0 && !criteria.ageGroups.includes("ALL")) {
          criteriaString += `${criteria.ageGroups.join(", ")}. `;
        }

        // Check and append supreme group criteria
        if (criteria.supremeGroups && criteria.supremeGroups.length > 0 && !criteria.supremeGroups.includes("ALL")) {
          criteriaString += `${criteria.supremeGroups.join(", ")}. `;
        }

        // Check and append levels criteria
        if (criteria.levels && criteria.levels.length > 0 && !criteria.levels.includes("ALL")) {
          criteriaString += `Levels: ${criteria.levels.join(", ")}. `;
        }

        // Check and append compete styles criteria
        if (criteria.competeStyles && criteria.competeStyles.length > 0 && !criteria.competeStyles.includes("ALL")) {
          criteriaString += `${criteria.competeStyles.join(", ")}. `;
        }

        // Check and append gender criteria
        if (criteria.genders && criteria.genders.length > 0) {
          // You might want to map these IDs to actual gender names if available
          criteriaString += `${criteria.genders.join(", ")}. `;
        }

           if (divisions.length && !divisions.includes("ALL")) {
      criteriaString += `${divisions.join(", ")}. `;
    } else if (divisionIds.length) {
      criteriaString += `Division ${divisionIds.join(", Division ")}. `;
    }
      }

      // Trim any trailing spaces and add to the results
      result.push(criteriaString.trim());
    });

    // Join the results into a single string
    return result.join("\n");
  };
   //#endregion =====================================GENERIC AWARD CONTESTANT CRITERIA GENERATED STRING END==========================================



  //#endregion===========================================GENERATING CRITERIA STRING HELPERS END====================================




  //#region============================CALCULATIONS START=============================================================================================

  const calculateContestantGrandTotalScoreFetched = (
    scores: ReceivedScoreBaseModel[] | undefined | null
  ): number => {
    if (!Array.isArray(scores)) {
      console.warn("Expected scores array, but got:", scores);
      return 0;
    }

    return scores.reduce((total, score) => {
      return total + (typeof score.score === "number" && !isNaN(score.score) ? score.score : 0);
    }, 0);
  };

  //Calculating each registered contestants Final Score for all aocs they competed in (WITH DROPPED SCORE LOGIC)
  const calculateFinalScoreWithDropsOrg = (aocId: number, contestantId: number): { finalScore: number; droppedScores: number[] } => {
    const contestantFetchedScores = allFetchedScores[contestantId] || [];
    if (!personnel || personnel.length === 0) {
      // Handle case where judges data is not available
      return {
        finalScore: 0,
        droppedScores: [],
      };
    }

    const judgeScores = judges.map(judge => {
      const totalScoreForJudge = contestantFetchedScores
        .filter(fetchedScore => fetchedScore.aocId === aocId && fetchedScore.judgeId === judge.eventJudgeId)
        .reduce((sum, score) => sum + (score.score || 0), 0);
      return totalScoreForJudge;
    });

    let droppedScores: number[] = [];

    // Drop Lowest Score
    if (supremeScoringSettings.dropLowestScore && judgeScores.length > 1) {
      const minScore = Math.min(...judgeScores);
      droppedScores.push(minScore);
      judgeScores.splice(judgeScores.indexOf(minScore), 1); // Remove lowest score
    }

    // Drop Highest Score
    if (supremeScoringSettings.dropHighestScore && judgeScores.length > 1) {
      const maxScore = Math.max(...judgeScores);
      droppedScores.push(maxScore);
      judgeScores.splice(judgeScores.indexOf(maxScore), 1); // Remove highest score
    }

    return {
      finalScore: judgeScores.reduce((sum, score) => sum + score, 0),
      droppedScores
    };
  };


  const calculateFinalScoreWithDrops = (
    aocId: number,
    contestantId: number
  ): { finalScore: number; droppedScores: number[] } => {
    const contestantFetchedScores = allFetchedScores[contestantId] || [];
    const assignedJudges = getJudgesForAoc(aocId);

    if (!assignedJudges.length) {
      return {
        finalScore: 0,
        droppedScores: [],
      };
    }

    const judgeScores = assignedJudges.map((judge: any) => {
      const judgeId =
        judge?.eventJudgeId ??
        judge?.judgeId ??
        judge?.personnelUserId ??
        judge?.userId ??
        judge?.id;

      return contestantFetchedScores
        .filter(score =>
          score.aocId === aocId &&
          score.judgeId === judgeId
        )
        .reduce((sum, score) => sum + (score.score || 0), 0);
    });

    const remainingScores = [...judgeScores];
    const droppedScores: number[] = [];

    if (supremeScoringSettings.dropLowestScore && remainingScores.length > 1) {
      const minScore = Math.min(...remainingScores);
      droppedScores.push(minScore);
      remainingScores.splice(remainingScores.indexOf(minScore), 1);
    }

    if (supremeScoringSettings.dropHighestScore && remainingScores.length > 1) {
      const maxScore = Math.max(...remainingScores);
      droppedScores.push(maxScore);
      remainingScores.splice(remainingScores.indexOf(maxScore), 1);
    }

    return {
      finalScore: remainingScores.reduce((sum, score) => sum + score, 0),
      droppedScores,
    };
  };




  const getJudgeId = (judge: any): number | undefined => {
    const id =
      judge?.eventJudgeId ??
      judge?.id ??
      judge?.judgeId ??
      judge?.personnelUserId ??
      judge?.userId;

    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };

  const sameId = (a: any, b: any) => String(a) === String(b);

  const calculateAOCTotalScore = (
    aocId: number,
    judgeIndex: number,
    contestantId: number
  ): number => {
    const scores = allFetchedScores[contestantId] ?? [];
    const assignedJudges = getJudgesForAoc(aocId);
    const judge = assignedJudges[judgeIndex];
    const judgeId = getJudgeId(judge);

    if (!judgeId) {
      console.warn("Missing judgeId", { aocId, judgeIndex, judge });
      return 0;
    }

    return scores
      .filter((s: any) =>
        sameId(s.aocId, aocId) &&
        sameId(s.judgeId, judgeId) &&
        sameId(s.registeredPageantContestantId, contestantId)
      )
      .reduce((sum: number, s: any) => sum + (Number(s.score) || 0), 0);
  };

  //Caluclating each registered contestants total score for each individual AOC (WITH DROPPED SCORE LOGIC)
  const calculateAOCFinalScoreWithDropsOrg = (aocId: number, contestantId: number): { finalScore: number; droppedScores: number[] } => {
    const assignedJudges = getJudgesForAoc(aocId);

    let judgeScores = assignedJudges.map((_, judgeIndex) =>
      calculateAOCTotalScore(aocId, judgeIndex, contestantId)
    );
    let droppedScores: number[] = [];

    // Handle dropLowestScore logic
    if (supremeScoringSettings.dropLowestScore && judgeScores.length > 1) {
      const minScore = Math.min(...judgeScores);
      const minIndexes = judgeScores.reduce<number[]>((acc, fetchedScore, index) => {
        if (fetchedScore === minScore) acc.push(index); // Collect all indices of the lowest score
        return acc;
      }, []);

      // Drop the first lowest score in case of ties
      droppedScores.push(judgeScores[minIndexes[0]]);
      judgeScores.splice(minIndexes[0], 1);  // Remove the first occurrence of the minimum score
    }

    // Handle dropHighestScore logic
    if (supremeScoringSettings.dropHighestScore && judgeScores.length > 1) {
      const maxScore = Math.max(...judgeScores);
      const maxIndexes = judgeScores.reduce<number[]>((acc, fetchedScore, index) => {
        if (fetchedScore === maxScore) acc.push(index); // Collect all indices of the highest score
        return acc;
      }, []);

      // Drop the first highest score in case of ties
      droppedScores.push(judgeScores[maxIndexes[0]]);
      judgeScores.splice(maxIndexes[0], 1);  // Remove the first occurrence of the maximum score
    }

    return {
      finalScore: judgeScores.reduce((total, score) => total + score, 0),  // Calculate the final score after dropping
      droppedScores,  // Return the dropped scores
    };
  };

  const calculateAOCFinalScoreWithDrops = (
    aocId: number,
    contestantId: number
  ) => {
    const assignedJudges = getJudgesForAoc(aocId);

    const judgeScores = assignedJudges.map((_, i) =>
      calculateAOCTotalScore(aocId, i, contestantId)
    );

    const remaining = [...judgeScores];
    const droppedScores: number[] = [];

    if (supremeScoringSettings.dropLowestScore && remaining.length > 1) {
      const min = Math.min(...remaining);
      droppedScores.push(min);
      remaining.splice(remaining.indexOf(min), 1);
    }

    if (supremeScoringSettings.dropHighestScore && remaining.length > 1) {
      const max = Math.max(...remaining);
      droppedScores.push(max);
      remaining.splice(remaining.indexOf(max), 1);
    }

    return {
      finalScore: remaining.reduce((sum, s) => sum + s, 0),
      droppedScores,
    };
  };
  //#endregion============================CALCULATIONS END===============================================================================================



  //#region======================HELPER METHODS START=====================================================================================================================


  /** Normalize to months; missing => +Infinity so they sort to the end */
  const toMonths = (age?: number, isMonths?: boolean): number =>
    age == null ? Number.POSITIVE_INFINITY : (isMonths ? age : age * 12);

  /** Optional int32 clamp if you really want to keep ints (Infinity passes through) */
  const int32 = (n: number): number => (Number.isFinite(n) ? (n | 0) : n);

  /**
   * Important: we shallow-copy each item (`{...g}`) so we sort plain objects,
   * not proxies. The array is also copied, so original state is untouched.
   */
  const orderPageantAgeGroupsYoungestToOldest = <T extends PageantAgeGroupBaseModel>(
    ageGroups: readonly T[] = []
  ): T[] => {
    return ageGroups
      .map(g => ({ ...g })) // <- unwrap proxy (shallow)
      .sort((a, b) => {
        const aStart = int32(toMonths(a.startAge, a.startAgeIsMonths));
        const bStart = int32(toMonths(b.startAge, b.startAgeIsMonths));
        if (aStart !== bStart) return aStart - bStart;

        // tie-breaker by end age (youngest end first)
        const aEnd = int32(toMonths(a.endAge, a.endAgeIsMonths));
        const bEnd = int32(toMonths(b.endAge, b.endAgeIsMonths));
        return aEnd - bEnd;
      });
  };
  //Checking if Scores are up to date
  const checkScoresUpToDate = () => {
    let allUpToDate = true; // Flag to track if all scores are up to date

    // Iterate through all contestants to check their scores
    for (const contestant of registeredContestants) {
      const contestantId = contestant.id;
      const contestantScores = allFetchedScores[contestantId] || [];
      const uploadedContestantScores = uploadedScores[contestantId] || {};  // Assuming uploadedScores structure

      // Check scores for each AOC
      for (const aoc of pageantAvailableAOC) {
        const uploadedAocScores = uploadedContestantScores[aoc.name] || {}; // Assuming aoc.name is the key

        // Calculate the total fetched score for this AOC
        const totalFetchedScore = contestantScores
          .filter(fetchedScore => fetchedScore.aocId === aoc.id) // Filter scores for the AOC
          .reduce((sum, fetchedScore) => sum + (fetchedScore.score || 0), 0); // Summing up scores for the AOC

        // Calculate the total uploaded score for this AOC
        const totalUploadedScore = Object.values(uploadedAocScores).reduce((sum, scoresByJudge) => {
          return sum + Object.values(scoresByJudge).reduce((judgeSum, score) => judgeSum + score, 0); // Summing judge scores for uploaded scores
        }, 0);

        // If any score is different, set allUpToDate to false and break the loop
        if (totalFetchedScore !== totalUploadedScore) {
          allUpToDate = false;
          break; // Exit the inner loop if a score is not up to date
        }
      }

      // Break the outer loop if we found any outdated scores
      if (!allUpToDate) break;
    }

    // After checking all contestants, show alert if all scores are up to date
    if (allUpToDate) {
      toast.info("All scores are up to date."); // Show alert if all scores are up to date
    }
  };
  //Highlight the selected AOCs that are being used for each contestant based on the supreme score settings
  const highlightAOCsUsedForMasterScoreNew = (contestantId: number): number[] => {
    // Get the scores for the specific contestant
    const contestantFetchedScores = allFetchedScores[contestantId] || [];


    type Score = {
      aocId: number;
      finalScoreWithDrops: number; // Final score after drops
      aocTypeId: string; // Assuming you are using string for AOC type IDs
      aocType: string;
    };

    // Collect all final scores for the contestant
    const allScores: Score[] = pageantAvailableAOC.map((aoc) => {
      // Get final score considering drops
      const { finalScore } = calculateFinalScoreWithDrops(aoc.id, contestantId);

      return {
        aocId: aoc.id,
        finalScoreWithDrops: finalScore,
        aocTypeId: String(aoc.aocTypeId), // Convert to string here
        aocType: aoc.aocType?.name || "unknown",
      };
    });

    let highlightedScores: Score[] = [];

    // Apply custom rules from supremeScoringSettings
    if (supremeScoringSettings && Array.isArray(supremeScoringSettings.customRules)) {
      supremeScoringSettings.customRules.forEach((rule) => {
        // console.log(`Processing rule:`, rule);
        let relevantScores = allScores.filter((score) => rule.aocIds.includes(score.aocId));

        // console.log(`Relevant scores before type limit:`, relevantScores);

        // Handle maxCountForSelectedAocType if defined in the rule
        if (rule.maxCountForSelectedAocType && rule.selectedAocType) {
          const selectedAocTypeId = rule.selectedAocType; // Keep this as a string
          const matchingScores = relevantScores.filter(score => score.aocTypeId === selectedAocTypeId);
          const otherScores = relevantScores.filter(score => score.aocTypeId !== selectedAocTypeId);

          // Sort matching scores to get the highest final score first
          const sortedMatchingScores = matchingScores.sort((a, b) => b.finalScoreWithDrops - a.finalScoreWithDrops);

          // Limit to the maximum count for the selected AOC type
          const limitedMatchingTypeAOCs = sortedMatchingScores.slice(0, rule.maxCountForSelectedAocType);

          // Combine limited matching scores with other scores
          relevantScores = [...limitedMatchingTypeAOCs, ...otherScores];
        }

        // Sort remaining relevant scores by final score with drops
        let sortedScores = relevantScores.sort((a, b) => b.finalScoreWithDrops - a.finalScoreWithDrops);

        // Add the top N scores according to the rule
        highlightedScores.push(...sortedScores.slice(0, rule.topN));
        // console.log(`Highlighted scores for rule:`, highlightedScores);
      });
    }

    // Return unique AOC IDs from highlighted scores
    const uniqueHighlightedAocIds = Array.from(new Set(highlightedScores.map(score => score.aocId)));
    // console.log(`Unique highlighted AOC IDs for contestant ${contestantId}:`, uniqueHighlightedAocIds);
    return uniqueHighlightedAocIds;
  };

  const getSupremeMaxPointsForContestant = (contestantId: number): number => {
    const highlightedAocIds = highlightAOCsUsedForMasterScoreNew(contestantId);

    return highlightedAocIds.reduce((sum, aocId) => {
      return sum + getAocMaxPoints(aocId, supremeScoringSettings, true);
    }, 0);
  };

  //Getting the SUpreme Groups for each Registered Contestant
  const supremeGroups = pageantStore.CurrentPageant.pageantAgeGroups?.filter((group) => group.isSupreme);
  const matchingSupremeGroups = (contestant) => {
    // Determine the check date based on whether fallback date is applied or not
    const checkDate = contestant.takenFallback && pageantStore.CurrentPageant?.fallbackDate
      ? pageantStore.CurrentPageant?.fallbackDate
      : pageantStore.CurrentPageant?.event?.startDate;

    // Initialize the contestant's age variables
    let ageInYears = 0;
    let ageInMonths = 0;

    // If the contestant is moving up an age group, use their current age group's start age
    if (contestant.moveUpAgeGroup && contestant.pageantAgeGroup) {
      const currentAgeGroupStartAge = contestant.pageantAgeGroup.startAge;
      const isStartAgeInMonths = contestant.pageantAgeGroup.startAgeIsMonths;

      // Handle the case where the age is based on months
      if (isStartAgeInMonths) {
        ageInYears = 0;  // Set years to 0 since the group is in months
        ageInMonths = currentAgeGroupStartAge;  // Set months to the start age of the group
      } else {
        // Handle the case where the age is based on years
        ageInYears = currentAgeGroupStartAge;
        ageInMonths = ageInYears * 12;  // Convert years to months
      }
    } else {
      // Otherwise, calculate the age normally based on the check date
      const { ageInYears: calculatedYears, ageInMonths: calculatedMonths } = calcAgeAsOfYearsMonthsNew(contestant.contestantUser?.birthday, checkDate);
      ageInYears = calculatedYears;
      ageInMonths = calculatedMonths;
    }

    const contestantGenderId = contestant.contestantUser?.genderId;

    // Filter supreme groups by both age and gender, include contestants in months for year-based groups
    return supremeGroups?.filter(group => {
      // Ensure gender matches
      if (group.genderId !== contestantGenderId) return false;

      // Ensure startAge and endAge are defined
      const { startAge, endAge } = group;
      if (startAge === undefined || endAge === undefined) return false;

      // Handle supreme groups defined by years
      if (!group.startAgeIsMonths) {
        return ageInYears >= startAge && ageInYears <= endAge;
      }

      // Handle supreme groups defined by months
      return ageInMonths >= startAge && ageInMonths <= endAge;
    });
  };

  //Number of Judges and the adjustment if scores are dropped
  const getAdjustedJudgeCountForAoc = (
    aocId: number,
    settings: ScoringCriteria = supremeScoringSettings
  ): number => {
    const judgeCount = getJudgeCountForAoc(aocId);

    const dropCount = getDropCount(settings, judgeCount);

    return Math.max(0, judgeCount - dropCount);
  };


  //#endregion======================HELPER METHODS END=====================================================================================================================

const normalizeAwardCriteria = (criteria: any, pageant: any) => {
  const divisions = pageant?.eventDivisions ?? pageant?.pageantDivisions ?? [];

  return {
    ageGroups: criteria.ageGroups?.length ? criteria.ageGroups : ["ALL"],
    ageGroupIds: (criteria.ageGroups || [])
      .map((name: string) => pageant?.pageantAgeGroups?.find((g: any) => getAgeGroupName(g) === name)?.id)
      .filter(Boolean) || [],

    supremeGroups: criteria.supremeGroups?.length ? criteria.supremeGroups : ["ALL"],
    supremeGroupIds: (criteria.supremeGroups || [])
      .map((name: string) => pageant?.pageantAgeGroups?.find((g: any) => getAgeGroupName(g) === name && g.isSupreme)?.id)
      .filter(Boolean) || [],

    division: criteria.division?.length
      ? criteria.division
      : criteria.divisions?.length
        ? criteria.divisions
        : ["ALL"],

    divisionIds: (criteria.division || criteria.divisions || [])
      .map((name: string) => divisions.find((d: any) => d.name === name)?.id)
      .filter(Boolean) || [],

    levels: criteria.levels?.length ? criteria.levels : ["ALL"],
    levelIds: (criteria.levels || [])
      .map((name: string) => pageant?.pageantLevelGroups?.find((l: any) => l.name === name)?.id)
      .filter(Boolean) || [],

    competeStyles: criteria.competeStyles?.length ? criteria.competeStyles : ["ALL"],
    competeStyleIds: (criteria.competeStyles || [])
      .map((name: string) => optionsCompeteStyles.find((style) => style.label === name)?.value)
      .filter(Boolean) || [],

    genders: criteria.genders?.length ? criteria.genders : ["ALL"],
  };
};

  // Fetch contestant name from contestantStore
  const fetchBasicContestantInfo = async (
    contestantId: number | null
  ): Promise<{ fullName: string; contestantNumber: string }> => {
    if (!contestantId) {
      return { fullName: "N/A", contestantNumber: "N/A" };
    }

    // Ensure the value is always an object of the correct structure
    const contestantInfo = contestantBasicInfoForPageant[contestantId];

    if (
      contestantInfo &&
      typeof contestantInfo === "object" &&
      "fullName" in contestantInfo &&
      "contestantNumber" in contestantInfo
    ) {
      return contestantInfo; // ✅ Return valid object
    }

    // Return default object if missing or invalid
    return { fullName: "N/A", contestantNumber: "N/A" };
  };


  const fetchTitleDetails = async (titleId: number): Promise<{ name: string; pullOrder: number }> => {
    try {
      const title = pageantStore?.CurrentPageant?.pageantAvailableTitles?.find((t: any) => t.id === titleId);
      return {
        name: title?.name ?? 'N/A',
        pullOrder: title?.pullOrder || 0,
      };
    } catch (error) {
      console.error(`Error fetching title details for ID ${titleId}:`, error);
      return { name: 'N/A', pullOrder: 0 };
    }
  };

  const registeredContestants = useMemo(() => {
    const raw = pageant?.registeredPageantContestants ?? [];
    const filtered = raw.filter(x => !x.isInterestedInEvent && !x.isDropOut);

    return filtered.map(contestant => {
      const supremeGroupsMatched = matchingSupremeGroups(contestant) || [];
      const supremeGroupIds = supremeGroupsMatched
        .map(group => group.id)
        .filter((id): id is number => id !== undefined);

      const supremeGroupNames = supremeGroupsMatched
        .map(group => getAgeGroupName(group as PageantAgeGroupBaseModel))
        .join(', ');

      const contestantId = contestant.id ?? 0;
      const contestantScores = allFetchedScores[contestantId] || [];

      const grandTotalScoreForContestant = calculateContestantGrandTotalScoreFetched(contestantScores);

      const highlightedAocIds = highlightAOCsUsedForMasterScoreNew(contestantId);

      const highlightedAocDetails = highlightedAocIds.map(aocId => {
        const aoc = pageantAvailableAOC.find(a => a.id === aocId);
        const { finalScore } = calculateFinalScoreWithDrops(aocId, contestantId);
        return {
          aocId,
          aocName: aoc?.name ?? 'Unknown',
          finalScore,
          contestantScores,
        };
      });

      return {
        id: contestantId,
        contestantNumber: contestant?.contestantNumber ?? '',
        firstName: contestant?.contestantUser?.firstName ?? '',
        lastName: contestant?.contestantUser?.lastName ?? '',
        fullName: `${contestant?.contestantUser?.firstName ?? ''} ${contestant?.contestantUser?.lastName ?? ''}`,
        ageGroup: getAgeGroupName(contestant?.pageantAgeGroup as PageantAgeGroupBaseModel) || 'N/A',
        supremeGroups: supremeGroupNames || 'N/A',
        supremeGroupIds,
        ageGroupId: contestant?.ageGroupId ?? 0,
        competeStyleId: contestant?.competeStyleId ?? 0,
        registeredContestantSelectedTitles: contestant?.registeredContestantSelectedTitles ?? [],
        hasWonTitle: contestant?.registeredContestantSelectedTitles?.some(title => title.isWon) || false,
        level: pageant?.pageantLevelGroups?.find(x => x.id === contestant.pageantLevelId)?.name ?? '',
        competeStyle: contestant?.competeStyleId
          ? ` ${optionsCompeteStyles.find(obj => obj.value === contestant?.competeStyleId)?.label}`
          : '',
        gender: contestant.pageantAgeGroup?.genderId === 1 ? 'Female' : 'Male',
        attributes: {
          id: contestantId,
          ageGroupId: contestant?.ageGroupId ?? 0,
          ageGroup: getAgeGroupName(contestant?.pageantAgeGroup as PageantAgeGroupBaseModel) || 'N/A',
          supremeGroups: supremeGroupNames || 'N/A',
          supremeGroupIds: supremeGroupIds,
          level: pageant?.pageantLevelGroups?.find(x => x.id === contestant.pageantLevelId)?.name ?? '',
          levelId: contestant.pageantLevelId || 0,
          gender: contestant.contestantUser?.genderId === 1 ? 'Female' : 'Male',
          genderId: contestant.contestantUser?.genderId,
          competeStyleId: contestant.competeStyleId,
        },
        grandTotalScore: grandTotalScoreForContestant,
        supremeTotalScore: {
          contestant,
          supremeTotalScore: contestant.tabulationSupremeScore ?? 0,
          highlightedAocCount: highlightedAocIds.length,
          highlightedAocs: highlightedAocDetails,
          isTie: false,
        },
        selectedPackageIds: contestant?.registeredContestantSelectedPackages?.map(x => x.pageantAvailablePackageId ?? 0) ?? [],
        selectedTitleIds: contestant?.registeredContestantSelectedTitles?.map(x => x.pageantAvailableTitleId ?? 0) ?? [],
        selectedEventIds: contestant?.registeredContestantSelectedEvents?.map(x => x.pageantAvailableEventId ?? 0) ?? [],
        mandatoryPackageIds: pageant?.pageantAvailablePackages?.filter(x => x.isMandatory && !x.isHiddenFromEventDetails).map(x => x.id ?? 0) ?? [],
        headshot: contestant?.headShot?.image || contestant.contestantUser?.profilePic?.image || undefined,
      };
    });
  }, [
    pageant,
    allFetchedScores,
    allSupremeScores,
    highlightAOCsUsedForMasterScoreNew,
    calculateFinalScoreWithDrops,
    supremeScoringSettings, // <-- ✅ ADD THIS
  ]);










  const queueContestantLiveRefresh = useCallback(
    (contestantId: number) => {
      if (!contestantId) return;

      pendingContestantRefreshIdsRef.current.add(contestantId);

      if (contestantRefreshTimeoutRef.current) {
        window.clearTimeout(contestantRefreshTimeoutRef.current);
      }

      contestantRefreshTimeoutRef.current = window.setTimeout(async () => {
        const ids = Array.from(pendingContestantRefreshIdsRef.current);
        pendingContestantRefreshIdsRef.current.clear();

        if (!ids.length) return;

        const pageantId = Number(pageant?.id ?? pageantStore?.CurrentPageant?.id ?? 0);
        if (!pageantId) return;

        try {
          const updatedContestants = await fetchRegisteredContestantsDynamic(
            {
              pageantId,
              registeredContestantIds: ids,
            },
            {
              replaceAll: false,
            }
          );

          setRegisteredContestantsNew((prev) => {
            const updatedById = new Map(
              updatedContestants.map((contestant: any) => [
                Number(contestant.id ?? contestant.registeredContestantId),
                contestant,
              ])
            );

            return prev.map((contestant) => {
              const id = Number((contestant as any).id ?? (contestant as any).registeredContestantId);
              return updatedById.get(id) ?? contestant;
            });
          });
        } catch (err) {
          console.error('[LiveScores] Failed to refresh contestant after score save', err);
        }
      }, 500);
    },
    [
      pageant?.id,
      pageantStore,
      fetchRegisteredContestantsDynamic,
      setRegisteredContestantsNew,
    ]
  );






  //#region==============================USE EFFECTS START=======================================================================================================================


  // useEffect(() => {
  //   if (!judges?.length || !pageantAvailableAOC?.length) return;

  //   console.group("🧑‍⚖️ Judges per AOC");

  //   pageantAvailableAOC.forEach(aoc => {
  //     const assignedJudges = getJudgesForAoc(aoc.id);

  //     console.log(
  //   "Full assigned judges:",
  //   assignedJudges.map(j => ({
  //     id: j?.eventJudgeId,
  //     eventJudgeId: j?.eventJudgeId,
  //     judgeId: j?.eventJudgeId,
  //     fullName: j?.judgeName,
  //     raw: j,
  //   }))
  // );

  // console.log(
  //   "Judge IDs:",
  //   assignedJudges.map(j =>
  //     j?.eventJudgeId
  //   )
  // );

  //     console.log(`AOC: ${aoc.name} (${aoc.id})`);
  //     console.log("Judge Count:", assignedJudges.length);
  //     console.log(
  //       "Judge IDs:",
  //       assignedJudges.map(j => j?.eventJudgeId)
  //     );
  //     console.log("Raw judge AOC assignments:", assignedJudges.map(j => getJudgeAocIds(j)));
  //     console.log("-----");
  //   });

  //   console.groupEnd();


  // }, [judges, pageantAvailableAOC]);




  // useEffect(() => {
  //   const storedJudgeNames = localStorage.getItem('customJudgeNames');
  //   if (storedJudgeNames && isScoringAndAdmin) {
  //     try {
  //       // Parse the stored JSON and set it as the custom judge names
  //       const parsedJudgeNames = JSON.parse(storedJudgeNames);
  //       setCustomJudgeNames(parsedJudgeNames);
  //     } catch (error) {
  //       console.error('Failed to parse custom judge names from localStorage:', error);
  //     }
  //   }
  // }, [setCustomJudgeNames]);
  //#endregion==============================USE EFFECTS END=======================================================================================================================

  const contextValue = useMemo(() => ({
    currentPageant,
    setCurrentPageant,

    personnel,
    setPersonnel,

    judges,
    setJudges,
    getJudgeCountForAoc,
    getAdjustedJudgeCountForAoc,
    getAocMaxPoints,
    getSupremeMaxPointsForContestant,

    ageGroupsOrdered,
    setAgeGroupsOrdered,

    supremeGroupsOrdered,
    setSupremeGroupsOrdered,

    eventDivisions,
    setEventDivisions,

    // aocs,
    // setAocs,

    pageantAvailableAOC,
    setPageantAvailableAOC,
    refetchPageantAvailableAOC,

    scoresForAOCCompare,
    setScoresForAOCCompare,
    allFetchedScores,
    setAllFetchedScores,
    refetchReceivedScores,
    allSupremeScores,
    setAllSupremeScores,
    refetchSupremeCriteriaAndSupremeScores,
    checkScoresUpToDate, // Add this property

    liveScoreNotifications,
setLiveScoreNotifications,
clearLiveScoreNotifications,

    // registeredContestants,

    registeredContestantsNew,
    setRegisteredContestantsNew,
    refetchRegisteredContestants,
    fetchRegisteredContestantsDynamic,
    updateEligibilityFlagsDynamic,

    supremeScoringSettings,
    setSupremeScoringSettings,
    handleSupremeScoreSetupNew,
    saveSupremeScoreCriteriaToStore,
    resetAwardScoringCriteriaDynamic,
    resetAwardRelationshipScoresDynamic,
    

    pageantAvailableAwards,
    refetchPageantAvailableAwards,
    setPageantAvailableAwards,
    awardRelationships,
    setAwardRelationships,
    refetchAwardRelationships,
    totalPointsPossible,
    setTotalPointsPossible,
    fetchAllStarted,
    setFetchAllStarted,
    isDataLoaded,
    setIsDataLoaded,
    existingAOCScoringCategories,
    setExistingAOCScoringCategories,
    calculateFinalScoreWithDrops,
    calculateAOCFinalScoreWithDrops,
    calculateAOCTotalScore,
    fetchTotalScoresWithDropsForContestantAsyncAll,
    isLoadingScoring,
    loadingText,
    showLoader,
    hideLoader,
    //====================STORES=================
    pageantStore,
    eventStore,
    userStore,
    pageantRegistrationStore,
    awardStore,
    tabulationStore,
    aocStore,
    judgeStore,
    pageant,
    selectedContestant,
    setSelectedContestant,

    sideAwardWinners,
    jackpotWinners,
    titleWinners,
    highlightAOCsUsedForMasterScoreNew,
    orderPageantAgeGroupsYoungestToOldest,

    fetchAllScoreAndTallyData,
    // fetchAllContestantScores,
    fetchContestantAttributes,
    isScoringAndAdmin,
    generateContestantCriteriaString,
    // customJudgeNames,
    // setCustomJudgeNames,
    // saveCustomJudgeNames,
    // getJudgeName,

    //====================AOC AND AWARD TYPES==================
    aocTypes,
    setAocTypes,
    awardTypes,
    setAwardTypes,
    //====================AWARD CRITERIA==================
    normalizeAwardCriteria,
    //====================AWARD CALCULATION==================
    runAwardRecalcSequence,
    cancelAwardRecalcSequence,
    awardCalcProgress,
    //====================HELPERS==================
    contestantBasicInfoForPageant,
    setContestantBasicInfoForPageant,
    fetchBasicContestantInfo,
    // fetchAllBasicContestantInfoForPageant,
    fetchTitleDetails,
    fetchContestantCriteriaAndScoringCriteria,

    //tabulation status
    tabulationStatus,
    setTabulationStatus,
    // refreshTabulationStatus

  }), [
    currentPageant,
    setCurrentPageant,

    personnel,
    setPersonnel,

    judges,
    setJudges,
    getAdjustedJudgeCountForAoc,
    getJudgeCountForAoc,
    getAdjustedJudgeCountForAoc,
    getAocMaxPoints,
    getSupremeMaxPointsForContestant,

    ageGroupsOrdered,
    setAgeGroupsOrdered,

    eventDivisions,
    setEventDivisions,

    supremeGroupsOrdered,
    setSupremeGroupsOrdered,

    // aocs,
    // setAocs,

    pageantAvailableAOC,
    setPageantAvailableAOC,
    refetchPageantAvailableAOC,

    scoresForAOCCompare,
    setScoresForAOCCompare,
    allFetchedScores,
    setAllFetchedScores,
    refetchReceivedScores,
    allSupremeScores,
    setAllSupremeScores,
    refetchSupremeCriteriaAndSupremeScores,
    saveSupremeScoreCriteriaToStore,
    resetAwardScoringCriteriaDynamic,
    resetAwardRelationshipScoresDynamic,

    checkScoresUpToDate, // Add checkScoresUpToDate here

        liveScoreNotifications,
setLiveScoreNotifications,
clearLiveScoreNotifications,
    // registeredContestants,

    registeredContestantsNew,
    setRegisteredContestantsNew,
    fetchRegisteredContestantsDynamic,
    refetchRegisteredContestants,
    updateEligibilityFlagsDynamic,

    supremeScoringSettings,
    setSupremeScoringSettings,
    handleSupremeScoreSetupNew,
    pageantAvailableAwards,
    setPageantAvailableAwards,
    refetchPageantAvailableAwards,
    awardRelationships,
    setAwardRelationships,
    refetchAwardRelationships,
    totalPointsPossible,
    setTotalPointsPossible,
    fetchAllStarted,
    setFetchAllStarted,
    isDataLoaded,
    setIsDataLoaded,
    existingAOCScoringCategories,
    setExistingAOCScoringCategories,
    calculateFinalScoreWithDrops,
    calculateAOCFinalScoreWithDrops,
    calculateAOCTotalScore,
    fetchTotalScoresWithDropsForContestantAsyncAll,
    isLoadingScoring,
    loadingText,
    showLoader,
    hideLoader,
    pageantStore,
    eventStore,
    userStore,
    pageantRegistrationStore,
    awardStore,
    tabulationStore,
    aocStore,
    judgeStore,
    pageant,
    selectedContestant,
    setSelectedContestant,

    fetchContestantAttributes,
    sideAwardWinners,
    jackpotWinners,
    titleWinners,
    highlightAOCsUsedForMasterScoreNew,
    orderPageantAgeGroupsYoungestToOldest,
    fetchAllScoreAndTallyData,
    // fetchAllContestantScores,
    isScoringAndAdmin,
    generateContestantCriteriaString,
    // customJudgeNames,
    // setCustomJudgeNames,
    // saveCustomJudgeNames,
    // getJudgeName,
    //====================AOC AND AWARD TYPES==================
    aocTypes,
    setAocTypes,
    awardTypes,
    setAwardTypes,
    //====================AWARD CRITERIA==================
    normalizeAwardCriteria,
    //====================AWARD CALCULATION==================
    runAwardRecalcSequence,
    cancelAwardRecalcSequence,
    awardCalcProgress,
    //====================HELPERS==================
    contestantBasicInfoForPageant,
    setContestantBasicInfoForPageant,
    fetchBasicContestantInfo,
    // fetchAllBasicContestantInfoForPageant,
    fetchTitleDetails,
    fetchContestantCriteriaAndScoringCriteria,
    //====================TABULATION STATUS - testing==================
    tabulationStatus,
    setTabulationStatus,
    //refreshTabulationStatus
  ]);

  return (
    <TabulationDataContext.Provider value={contextValue}>
      {children}
    </TabulationDataContext.Provider>
  );


};
